import { z } from 'zod';

export const REPORT_CATALOG_SCHEMA_VERSION = 1;
export const REPORTING_MANIFEST_PROTOCOL_VERSION = 1;
export const REPORT_ROLLOUT_AUDIENCES = Object.freeze(['all', 'canary', 'internal']);
export const REPORT_CLASSES = Object.freeze([
    'generic-list',
    'portfolio',
    'dossier',
    'statutory-template',
    'technical-ops',
]);
export const REPORT_RENDERERS = Object.freeze([
    'programmatic-list-v1',
    'programmatic-structured-v2',
    'programmatic-structured-v3',
    'static-repx-v1',
]);
export const REPORT_SCOPE_MODES = Object.freeze(['filtered', 'selected', 'single', 'tenant']);
export const REPORT_OUTPUT_FORMATS = Object.freeze(['pdf', 'docx', 'xlsx']);
export const REPORT_MENU_VISIBILITIES = Object.freeze(['direct', 'configuration-only', 'hidden']);

const stableKey = z.string().trim().min(1).max(120).regex(/^[a-z][a-z0-9-]*$/);
const stableIdentifier = z.string().trim().min(1).max(120).regex(/^[a-z][A-Za-z0-9]*$/);
const uniqueArray = (schema, max) => z.array(schema).min(1).max(max).refine(
    (values) => new Set(values).size === values.length,
    { message: 'Values must be unique' },
);

const ReportCapabilitiesSchema = z.object({
    profile: z.boolean().default(false),
    profileOptional: z.boolean().default(false),
    interactive: z.boolean().default(false),
    schedule: z.boolean().default(false),
    archive: z.boolean().default(false),
    snapshot: z.boolean().default(false),
    compare: z.boolean().default(false),
    aiNarrative: z.boolean().default(false),
    designer: z.boolean().default(false),
}).strict();

const ReportSelectorSchema = z.object({
    properties: z.record(
        z.string().trim().min(1).max(120),
        z.enum(['tenant-owned-object-id', 'string', 'number', 'boolean']),
    ).default({}),
    required: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
    additionalProperties: z.literal(false),
}).strict().superRefine((value, ctx) => {
    const properties = new Set(Object.keys(value.properties));
    for (const required of value.required) {
        if (!properties.has(required)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['required'], message: 'Required selectors must be declared as properties' });
        }
    }
});

const EmptyReportSelectorSchema = Object.freeze({
    properties: Object.freeze({}),
    required: Object.freeze([]),
    additionalProperties: false,
});

const ReportCompositionSchema = z.object({
    moduleKey: stableKey,
    moduleVersion: z.number().int().positive(),
    inputContractKey: stableKey,
    inputContractVersion: z.number().int().positive(),
    documentSpecVersion: z.number().int().positive(),
}).strict();

const ReportProfileContractSchema = z.object({
    schemaKey: stableKey,
    schemaVersion: z.number().int().positive(),
    optional: z.boolean(),
}).strict();

const ReportArtifactPolicySchema = z.object({
    archiveEnabled: z.boolean(),
    canonicalFormat: z.literal('pdf'),
    accessPolicy: z.enum(['source-current-v1', 'controlled-recipient-v1']),
}).strict();

export const ReportDefinitionSchema = z.object({
    key: stableKey,
    definitionVersion: z.number().int().positive(),
    surfaceKey: stableKey,
    domainKey: stableKey,
    presentation: z.object({
        labelKey: z.string().trim().min(1).max(160),
        descriptionKey: z.string().trim().min(1).max(160).optional(),
        order: z.number().int().min(0).max(1000),
        menuVisibility: z.enum(REPORT_MENU_VISIBILITIES).default('direct'),
    }).strict(),
    lifecycle: z.object({
        runEnabled: z.boolean(),
        scheduleEnabled: z.boolean(),
        readExistingArtifacts: z.boolean(),
        rolloutAudience: z.enum(REPORT_ROLLOUT_AUDIENCES),
    }).strict(),
    rollout: z.object({
        owner: stableKey,
        flag: z.string().trim().min(1).max(160).regex(/^[A-Z][A-Z0-9_]*$/),
        minimumCanaryRuns: z.number().int().nonnegative().max(1_000_000),
        minimumCanaryDays: z.number().int().nonnegative().max(365),
        maximumAuthorizationMismatches: z.number().int().nonnegative().max(10_000),
    }).strict(),
    legacyCompatibility: z.literal('none'),
    reportClass: z.enum(REPORT_CLASSES),
    renderer: z.object({
        key: z.enum(REPORT_RENDERERS),
        contractVersion: z.number().int().positive(),
        layoutName: z.string().regex(/^[A-Za-z0-9_-]+\.repx$/).optional(),
        layoutDigest: z.string().regex(/^[a-f0-9]{64}$/).optional(),
        queryKey: stableKey.optional(),
        queryDigest: z.string().regex(/^[a-f0-9]{64}$/).optional(),
        rootProperty: z.string().trim().min(1).max(120).regex(/^[A-Za-z_][A-Za-z0-9_]*$/).optional(),
        dataShape: z.enum(['object-array-root', 'object-root']).optional(),
    }).strict(),
    composition: ReportCompositionSchema.optional(),
    profile: ReportProfileContractSchema.optional(),
    artifact: ReportArtifactPolicySchema.optional(),
    selectorSchema: ReportSelectorSchema.default(EmptyReportSelectorSchema),
    selectorDefaults: z.record(
        z.string().trim().min(1).max(120),
        z.union([z.string().max(500), z.number().finite(), z.boolean()]),
    ).default({}),
    sourcePageKey: z.string().trim().min(1).max(160),
    sourceSurfaces: z.record(stableKey, z.string().trim().min(1).max(160)).optional(),
    scopeModes: uniqueArray(z.enum(REPORT_SCOPE_MODES), REPORT_SCOPE_MODES.length),
    outputFormats: uniqueArray(z.enum(REPORT_OUTPUT_FORMATS), REPORT_OUTPUT_FORMATS.length),
    visibleFields: z.array(z.string().trim().min(1).max(80)).max(100).refine(
        (values) => new Set(values).size === values.length,
        { message: 'Visible fields must be unique' },
    ),
    limits: z.object({
        maxRecords: z.number().int().positive().max(25_000),
        maxSelectedRecords: z.number().int().positive().max(5_000).optional(),
        maxPayloadBytes: z.number().int().positive().max(10_485_760).optional(),
    }).strict(),
    archiveType: stableIdentifier.nullable(),
    capabilities: ReportCapabilitiesSchema,
}).strict().superRefine((definition, ctx) => {
    const { capabilities, lifecycle } = definition;

    if (lifecycle.scheduleEnabled && !lifecycle.runEnabled) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['lifecycle', 'scheduleEnabled'], message: 'Scheduling requires report runs to be enabled' });
    }
    if (capabilities.profileOptional && !capabilities.profile) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['capabilities', 'profileOptional'], message: 'Optional profiles require profile capability' });
    }
    if (lifecycle.scheduleEnabled && !capabilities.schedule) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['lifecycle', 'scheduleEnabled'], message: 'Scheduling is not supported by this report' });
    }
    if (definition.renderer.key === 'programmatic-structured-v3') {
        if (!definition.composition || !definition.profile || !definition.artifact?.archiveEnabled) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['composition'], message: 'Programmatic v3 reports require composition, profile and archive metadata' });
        }
        if (!capabilities.profile || capabilities.profileOptional !== definition.profile?.optional) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['profile'], message: 'Programmatic v3 profile metadata must match report capabilities' });
        }
        if (!capabilities.archive || !definition.artifact?.archiveEnabled) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['artifact'], message: 'Programmatic v3 reports require archive capability' });
        }
        if (definition.renderer.layoutDigest || definition.renderer.layoutName || definition.renderer.queryKey
            || definition.renderer.queryDigest || definition.renderer.rootProperty || definition.renderer.dataShape) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['renderer'], message: 'Programmatic v3 reports cannot declare REPX or query metadata' });
        }
    } else {
        if (definition.composition || definition.profile || definition.artifact) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['composition'], message: 'Legacy renderers cannot declare v3 composition, profile or artifact metadata' });
        }
        if (capabilities.archive !== Boolean(definition.archiveType)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['archiveType'], message: 'archiveType must match the archive capability' });
        }
    }
    if ((capabilities.compare || capabilities.aiNarrative) && !capabilities.snapshot) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['capabilities', 'snapshot'], message: 'Comparison and AI narrative require snapshots' });
    }
    if (definition.renderer.key === 'static-repx-v1') {
        if (!definition.renderer.layoutDigest || !definition.renderer.layoutName || !definition.renderer.queryKey || !definition.renderer.queryDigest
            || !definition.renderer.rootProperty || !definition.renderer.dataShape) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['renderer'], message: 'Static REPX reports require an allowlisted layout, layout digest, query descriptor, root property and data shape' });
        }
        if (!definition.limits.maxPayloadBytes) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['limits', 'maxPayloadBytes'], message: 'Static REPX reports require a payload byte limit' });
        }
        if (definition.capabilities.archive || definition.capabilities.schedule || definition.lifecycle.scheduleEnabled) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['capabilities'], message: 'Static REPX canary reports cannot enable archive or schedule' });
        }
    } else if (definition.renderer.layoutDigest || definition.renderer.layoutName || definition.renderer.queryKey
        || definition.renderer.queryDigest || definition.renderer.rootProperty || definition.renderer.dataShape) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['renderer'], message: 'Only static REPX reports may declare layout and query metadata' });
    }
    if (definition.sourceSurfaces
        && definition.sourceSurfaces[definition.surfaceKey] !== definition.sourcePageKey) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sourceSurfaces'], message: 'Source surfaces must bind the primary surface to its canonical source page' });
    }
    const selectorProperties = new Set(Object.keys(definition.selectorSchema.properties));
    if (Object.keys(definition.selectorDefaults).some((key) => !selectorProperties.has(key))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['selectorDefaults'], message: 'Selector defaults must be declared by selectorSchema' });
    }
    if (definition.reportClass === 'dossier'
        && (definition.scopeModes.length !== 1 || definition.scopeModes[0] !== 'single')) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scopeModes'], message: 'Dossier reports require single-record scope' });
    }
    if (definition.reportClass === 'portfolio'
        && definition.scopeModes.some((mode) => !['filtered', 'selected'].includes(mode))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scopeModes'], message: 'Portfolio reports support filtered or selected scope only' });
    }
});

function deepFreeze(value) {
    if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
    Object.values(value).forEach(deepFreeze);
    return Object.freeze(value);
}

export function validateReportCatalog(rawCatalog) {
    const parsedEntries = Object.entries(rawCatalog).map(([catalogKey, rawDefinition]) => {
        const definition = ReportDefinitionSchema.parse(rawDefinition);
        if (catalogKey !== definition.key) {
            throw new Error(`Report catalog key mismatch: ${catalogKey} !== ${definition.key}`);
        }
        return [catalogKey, definition];
    });

    const archiveTypes = new Set();
    const surfaceOrders = new Set();
    for (const [, definition] of parsedEntries) {
        if (definition.archiveType) {
            if (archiveTypes.has(definition.archiveType)) {
                throw new Error(`Duplicate report archive type: ${definition.archiveType}`);
            }
            archiveTypes.add(definition.archiveType);
        }
        const surfaceOrder = `${definition.surfaceKey}:${definition.presentation.order}`;
        if (surfaceOrders.has(surfaceOrder)) {
            throw new Error(`Duplicate report surface order: ${surfaceOrder}`);
        }
        surfaceOrders.add(surfaceOrder);
    }

    return deepFreeze(Object.fromEntries(parsedEntries));
}

export function reportDefinitionSupportsSurface(definition, surfaceKey) {
    if (!definition || !surfaceKey) return false;
    if (definition.sourceSurfaces) return Object.hasOwn(definition.sourceSurfaces, surfaceKey);
    return definition.surfaceKey === surfaceKey;
}

export function reportDefinitionSourcePageKey(definition, surfaceKey) {
    if (!reportDefinitionSupportsSurface(definition, surfaceKey)) return null;
    return definition.sourceSurfaces?.[surfaceKey] || definition.sourcePageKey;
}

export function buildReportManifestDefinition(
    definition,
    current = REPORT_CATALOG[definition.key]?.definitionVersion === definition.definitionVersion,
) {
    return {
        reportKey: definition.key,
        definitionVersion: definition.definitionVersion,
        current,
        rendererKey: definition.renderer.key,
        rendererContractVersion: definition.renderer.contractVersion,
        layoutName: definition.renderer.layoutName ?? null,
        layoutDigest: definition.renderer.layoutDigest ?? null,
        queryKey: definition.renderer.queryKey ?? null,
        queryDigest: definition.renderer.queryDigest ?? null,
        rootProperty: definition.renderer.rootProperty ?? null,
        dataShape: definition.renderer.dataShape ?? null,
        selectorSchema: definition.selectorSchema,
        selectorDefaults: definition.selectorDefaults,
        reportClass: definition.reportClass,
        legacyCompatibility: definition.legacyCompatibility,
        scopeModes: [...definition.scopeModes],
        outputFormats: [...definition.outputFormats],
        visibleFields: [...definition.visibleFields],
        maxRecords: definition.limits.maxRecords,
        maxSelectedRecords: definition.limits.maxSelectedRecords ?? null,
        maxPayloadBytes: definition.limits.maxPayloadBytes ?? null,
        runEnabled: definition.lifecycle.runEnabled,
        scheduleEnabled: definition.lifecycle.scheduleEnabled,
        readExistingArtifacts: definition.lifecycle.readExistingArtifacts,
        rollout: definition.rollout,
        composition: definition.composition ?? null,
        profile: definition.profile ?? null,
        artifact: definition.artifact ?? null,
        capabilities: {
            profile: definition.capabilities.profile,
            profileOptional: definition.capabilities.profileOptional,
            interactive: definition.capabilities.interactive,
            schedule: definition.capabilities.schedule,
            archive: definition.capabilities.archive,
            snapshot: definition.capabilities.snapshot,
            compare: definition.capabilities.compare,
            aiNarrative: definition.capabilities.aiNarrative,
            designer: definition.capabilities.designer,
        },
    };
}

export function buildReportManifestPayload(catalogOrDefinitions) {
    const isDefinitionHistory = Array.isArray(catalogOrDefinitions);
    const definitions = isDefinitionHistory
        ? catalogOrDefinitions
        : Object.values(catalogOrDefinitions);
    const currentVersions = isDefinitionHistory
        ? new Map(Object.values(REPORT_CATALOG).map((definition) => [definition.key, definition.definitionVersion]))
        : new Map(definitions.map((definition) => [definition.key, definition.definitionVersion]));
    return {
        protocolVersion: REPORTING_MANIFEST_PROTOCOL_VERSION,
        catalogSchemaVersion: REPORT_CATALOG_SCHEMA_VERSION,
        definitions: [...definitions]
            .sort((left, right) => left.key.localeCompare(right.key)
                || left.definitionVersion - right.definitionVersion)
            .map((definition) => buildReportManifestDefinition(
                definition,
                currentVersions.get(definition.key) === definition.definitionVersion,
            )),
    };
}

const BIA_RELATION_SELECTOR_SCHEMA = {
    properties: { biaId: 'tenant-owned-object-id' },
    required: ['biaId'],
    additionalProperties: false,
};

function registerTableDefinition({ key, definitionVersion = 2, surfaceKey, domainKey, labelKey, sourcePageKey, visibleFields, scopeModes = ['filtered', 'selected'], selectorSchema, order = 0, maxRecords = 5_000 }) {
    return {
        key,
        definitionVersion,
        surfaceKey,
        domainKey,
        presentation: { labelKey, order },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: `REPORT_DEFINITION_${key.replaceAll('-', '_').toUpperCase()}`, minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'generic-list',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: key, moduleVersion: 1, inputContractKey: `${key}-input`, inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        ...(selectorSchema ? { selectorSchema } : {}),
        sourcePageKey,
        scopeModes,
        outputFormats: ['pdf', 'docx', 'xlsx'],
        visibleFields,
        limits: { maxRecords, ...(scopeModes.includes('selected') ? { maxSelectedRecords: Math.min(maxRecords, 1_000) } : {}), maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    };
}

const rawReportCatalog = {
    'risk-filtered': {
        key: 'risk-filtered',
        definitionVersion: 3,
        surfaceKey: 'risk-register',
        domainKey: 'risk',
        presentation: { labelKey: 'reportCatalog.riskFiltered', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_RISK_FILTERED', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'generic-list',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'risk-filtered', moduleVersion: 1, inputContractKey: 'risk-filtered-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'riskOperationsRiskRegister',
        scopeModes: ['filtered', 'selected'],
        outputFormats: ['pdf', 'docx', 'xlsx'],
        visibleFields: [
            'riskId', 'riskNature', 'title', 'priority', 'probability', 'impact',
            'score', 'riskLevel', 'status', 'responseStrategy', 'owners',
            'riskNarrative',
            'financialImpact',
            'annualizedLoss',
        ],
        limits: { maxRecords: 25_000, maxSelectedRecords: 5_000, maxPayloadBytes: 10_485_760 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'asset-filtered': {
        key: 'asset-filtered',
        definitionVersion: 3,
        surfaceKey: 'asset-register',
        domainKey: 'asset',
        presentation: { labelKey: 'reportCatalog.assetFiltered', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ASSET_FILTERED', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'generic-list',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'asset-filtered', moduleVersion: 1, inputContractKey: 'asset-filtered-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'assetInventoryAssets',
        scopeModes: ['filtered', 'selected'],
        outputFormats: ['pdf', 'docx', 'xlsx'],
        visibleFields: [
            'assetId', 'assetName', 'category', 'assetCategory', 'assetGroup',
            'classificationLevel', 'usingStatus', 'owners', 'location',
            'purchaseDate', 'currentValue', 'currency',
            'definition',
            'confidentialityValue',
            'integrityValue',
            'availabilityValue',
            'amount',
            'dependentBeing',
            'containsPersonalData',
            'rto',
            'rpo',
            'mtpd',
        ],
        limits: { maxRecords: 25_000, maxSelectedRecords: 5_000, maxPayloadBytes: 10_485_760 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'audit-findings': {
        key: 'audit-findings',
        definitionVersion: 3,
        surfaceKey: 'audit-assessment',
        domainKey: 'audit',
        presentation: { labelKey: 'reportCatalog.auditFindings', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_AUDIT_FINDINGS', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'audit-findings', moduleVersion: 1, inputContractKey: 'audit-findings-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'simple-static-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'auditFinding',
        scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['name', 'control_name', 'severity', 'status', 'is_overdue', 'overdue_label', 'assignee', 'deadline', 'reference_number', 'clause', 'responsible_unit', 'finding_narrative'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'auditor-findings': {
        key: 'auditor-findings',
        definitionVersion: 2,
        surfaceKey: 'audit-assessment',
        domainKey: 'audit',
        presentation: { labelKey: 'reportCatalog.auditorFindings', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_AUDITOR_FINDINGS', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'auditor-findings', moduleVersion: 1, inputContractKey: 'auditor-findings-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'simple-static-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'auditFinding',
        scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['auditor_id', 'auditor_name', 'opened_count', 'closed_count', 'open_count', 'avg_closure_days', 'closure_rate'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'document-revisions': {
        key: 'document-revisions',
        definitionVersion: 2,
        surfaceKey: 'report-document',
        domainKey: 'document',
        presentation: { labelKey: 'reportCatalog.documentRevisions', order: 0, menuVisibility: 'configuration-only' },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_DOCUMENT_REVISIONS', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'document-revisions', moduleVersion: 1, inputContractKey: 'document-revisions-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'simple-static-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'reportDocument',
        scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['code', 'title', 'version', 'owner', 'next_review', 'days_remaining'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'document-pending': {
        key: 'document-pending',
        definitionVersion: 2,
        surfaceKey: 'report-document',
        domainKey: 'document',
        presentation: { labelKey: 'reportCatalog.documentPending', order: 10, menuVisibility: 'configuration-only' },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_DOCUMENT_PENDING', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'document-pending', moduleVersion: 1, inputContractKey: 'document-pending-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'simple-static-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'reportDocument',
        scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['code', 'title', 'version', 'owner', 'current_step', 'total_steps', 'waiting_for', 'pending_since'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'document-filtered': {
        key: 'document-filtered',
        definitionVersion: 3,
        surfaceKey: 'report-document',
        domainKey: 'document',
        presentation: { labelKey: 'reportCatalog.documentFiltered', order: 20 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_DOCUMENT_FILTERED', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'generic-list',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'document-filtered', moduleVersion: 1, inputContractKey: 'document-filtered-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'reportDocument',
        scopeModes: ['filtered', 'selected'],
        outputFormats: ['pdf', 'docx', 'xlsx'],
        visibleFields: ['code', 'title', 'type', 'classification', 'unit', 'status', 'workflow', 'review', 'description', 'documentMetadata', 'createdAt', 'updatedAt', 'tags', 'effectivePeriod'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'document-detailed-portfolio': {
        key: 'document-detailed-portfolio',
        definitionVersion: 3,
        surfaceKey: 'report-document',
        domainKey: 'document',
        presentation: { labelKey: 'reportCatalog.documentPortfolio', order: 30, menuVisibility: 'configuration-only' },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_DOCUMENT_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'document-detailed-portfolio', moduleVersion: 1, inputContractKey: 'document-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'reportDocument',
        scopeModes: ['filtered', 'selected'],
        outputFormats: ['pdf', 'docx'],
        visibleFields: ['code', 'title', 'type', 'classification', 'unit', 'status', 'workflow', 'review', 'description', 'documentMetadata', 'createdAt', 'updatedAt', 'tags', 'effectivePeriod'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'document-security': {
        key: 'document-security',
        definitionVersion: 2,
        surfaceKey: 'document-detail',
        domainKey: 'document',
        presentation: { labelKey: 'reportCatalog.documentSecurity', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_DOCUMENT_SECURITY', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'technical-ops',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'document-security', moduleVersion: 1, inputContractKey: 'document-security-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'simple-static-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        selectorSchema: {
            properties: { documentId: 'tenant-owned-object-id' },
            required: ['documentId'],
            additionalProperties: false,
        },
        sourcePageKey: 'reportDocument',
        scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['user_name', 'path', 'ip', 'timestamp'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'control-maturity': {
        key: 'control-maturity',
        definitionVersion: 2,
        surfaceKey: 'isms-controls-soa',
        domainKey: 'isms-controls',
        presentation: { labelKey: 'reportCatalog.controlMaturity', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_CONTROL_MATURITY', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'control-maturity', moduleVersion: 1, inputContractKey: 'control-maturity-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'ismsControlsSoa',
        scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['scf_id', 'name', 'domain', 'level', 'level_label'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'iso-compliance-summary': {
        key: 'iso-compliance-summary',
        definitionVersion: 2,
        surfaceKey: 'isms-controls-soa',
        domainKey: 'isms-controls',
        presentation: { labelKey: 'reportCatalog.isoComplianceSummary', order: 20 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ISO_COMPLIANCE_SUMMARY', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'iso-compliance-summary', moduleVersion: 1, inputContractKey: 'iso-compliance-summary-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'ismsControlsSoa',
        scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['clause_code', 'title', 'compliance_rate', 'missing_docs', 'missing_controls', 'open_findings'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'soa': {
        key: 'soa',
        definitionVersion: 4,
        surfaceKey: 'isms-controls-soa',
        domainKey: 'isms-controls',
        presentation: { labelKey: 'reportCatalog.soa', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_SOA', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'soa', moduleVersion: 1, inputContractKey: 'soa-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'ismsControlsSoa',
        scopeModes: ['tenant'],
        outputFormats: ['pdf'],
        visibleFields: [
            'control_no', 'control_name', 'domain_identifier', 'tenant_overview_status',
            'maturity_line', 'objective_summary', 'related_documents_text', 'comment',
            'objective_comments_text',
        ],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'bigdes-gap': {
        key: 'bigdes-gap', definitionVersion: 2,
        surfaceKey: 'bigdes-current-situation-analysis-gap', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesGap', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_GAP', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bigdes-gap', moduleVersion: 1, inputContractKey: 'bigdes-gap-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        selectorSchema: { properties: { applicabilityId: 'tenant-owned-object-id' }, required: ['applicabilityId'], additionalProperties: false },
        sourcePageKey: 'bigdesCurrentSituationAnalysis', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: [
            'tenant_display_name', 'tenant_logo_url', 'asset_group_name', 'asset_group_no', 'category',
            'category_label_tr', 'criticality_degree', 'generated_date', 'measure_code', 'measure_title',
            'link_source_type', 'link_source_key', 'implementation_status', 'implementation_status_short',
            'implementation_status_label_tr', 'current_explanation', 'target_status', 'target_status_short',
            'target_status_label_tr', 'target_required_work', 'compensatory_text',
        ],
        limits: { maxRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'bigdes-current-situation-analysis-summary': {
        key: 'bigdes-current-situation-analysis-summary', definitionVersion: 2,
        surfaceKey: 'bigdes-current-situation-analysis', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesCurrentSituationAnalysisSummary', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_CURRENT_SITUATION_ANALYSIS_SUMMARY', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bigdes-current-situation-analysis-summary', moduleVersion: 1, inputContractKey: 'bigdes-current-situation-analysis-summary-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'bigdesCurrentSituationAnalysis', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: [
            'tenant_display_name', 'tenant_logo_url', 'generated_date', 'category_label_tr', 'asset_group_name',
            'asset_group_no', 'criticality_degree', 'required_measure_count', 'evaluated_measure_count',
            'fully_measure_count', 'mostly_measure_count', 'partial_measure_count', 'none_measure_count',
            'not_applicable_measure_count',
        ],
        limits: { maxRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'bigdes-current-situation-evaluation-summary': {
        key: 'bigdes-current-situation-evaluation-summary', definitionVersion: 2,
        surfaceKey: 'bigdes-current-situation-evaluation', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesCurrentSituationEvaluationSummary', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_CURRENT_SITUATION_EVALUATION_SUMMARY', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bigdes-current-situation-evaluation-summary', moduleVersion: 1, inputContractKey: 'bigdes-current-situation-evaluation-summary-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'bigdesCurrentSituationEvaluation', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: [
            'tenant_display_name', 'tenant_logo_url', 'generated_date', 'category_label_tr', 'asset_group_name',
            'asset_group_no', 'criticality_degree', 'required_measure_count', 'effective_measure_count',
            'not_effective_measure_count', 'partially_effective_measure_count',
        ],
        limits: { maxRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'bigdes-survey-evaluation': {
        key: 'bigdes-survey-evaluation', definitionVersion: 2,
        surfaceKey: 'bigdes-asset-group-evaluation', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesSurveyEvaluation', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_SURVEY_EVALUATION', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bigdes-survey-evaluation', moduleVersion: 1, inputContractKey: 'bigdes-survey-evaluation-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        selectorSchema: {
            properties: { evaluationId: 'tenant-owned-object-id', round: 'number' },
            required: ['evaluationId', 'round'], additionalProperties: false,
        },
        sourcePageKey: 'bigdesAssetGroupEvaluation', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: [
            'tenant_display_name', 'tenant_logo_url', 'asset_group_name', 'generated_date', 'participant_name',
            'participant_title', 'participant_unit', 'participant_email', 'completed_at', 'total_score',
            'criticality_degree', 'answers_detail', 'survey_completion_date', 'coordinator_name',
            'approval_criticality_degree', 'approval_date', 'approval_authority_name', 'approval_signature',
        ],
        limits: { maxRecords: 5_000, maxPayloadBytes: 4_194_304 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'bigdes-measure-applicability': {
        key: 'bigdes-measure-applicability', definitionVersion: 2,
        surfaceKey: 'bigdes-measure-applicability', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesMeasureApplicability', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_MEASURE_APPLICABILITY', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bigdes-measure-applicability', moduleVersion: 1, inputContractKey: 'bigdes-measure-applicability-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'bigdesMeasureApplicability', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: [
            'tenant_display_name', 'tenant_logo_url', 'generated_date', 'category_label_tr', 'asset_group_name',
            'asset_group_no', 'criticality_degree', 'in_audit_scope_label_tr',
            'personal_data_security_label_tr', 'instant_messaging_security_label_tr', 'cloud_computing_security_label_tr',
            'crypto_applications_security_label_tr', 'critical_infrastructure_security_label_tr',
            'new_developments_procurement_label_tr', 'os_hardening_label_tr', 'db_hardening_label_tr',
            'server_hardening_label_tr',
        ],
        limits: { maxRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'bigdes-implementation-process-evaluation': {
        key: 'bigdes-implementation-process-evaluation', definitionVersion: 2,
        surfaceKey: 'bigdes-application-process-evaluation', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesImplementationProcessEvaluation', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_IMPLEMENTATION_PROCESS_EVALUATION', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bigdes-implementation-process-evaluation', moduleVersion: 1, inputContractKey: 'bigdes-implementation-process-evaluation-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'bigdesApplicationProcessEvaluation', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: [
            'tenant_display_name', 'tenant_logo_url', 'generated_date', 'question_no', 'audit_question_tr',
            'audit_methods_label_tr', 'work_form_no', 'effectiveness_status_label_tr',
        ],
        limits: { maxRecords: 9, maxPayloadBytes: 524_288 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'bigdes-audit-confidentiality-commitment': {
        key: 'bigdes-audit-confidentiality-commitment', definitionVersion: 2,
        surfaceKey: 'bigdes-audit-reports', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesAuditConfidentialityCommitment', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_AUDIT_CONFIDENTIALITY_COMMITMENT', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bigdes-audit-confidentiality-commitment', moduleVersion: 1, inputContractKey: 'bigdes-audit-confidentiality-commitment-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'bigdesAuditReports', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['tenant_display_name', 'tenant_logo_url', 'generated_date'],
        limits: { maxRecords: 1, maxPayloadBytes: 262_144 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'bigdes-audit-impartiality-commitment': {
        key: 'bigdes-audit-impartiality-commitment', definitionVersion: 2,
        surfaceKey: 'bigdes-audit-reports', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesAuditImpartialityCommitment', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_AUDIT_IMPARTIALITY_COMMITMENT', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bigdes-audit-impartiality-commitment', moduleVersion: 1, inputContractKey: 'bigdes-audit-impartiality-commitment-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'bigdesAuditReports', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['tenant_display_name', 'tenant_logo_url', 'generated_date'],
        limits: { maxRecords: 1, maxPayloadBytes: 262_144 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'bigdes-current-situation-evaluation': {
        key: 'bigdes-current-situation-evaluation',
        definitionVersion: 2,
        surfaceKey: 'bigdes-current-situation-evaluation-detail',
        domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesCurrentSituationEvaluation', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_CURRENT_SITUATION_EVALUATION', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bigdes-current-situation-evaluation', moduleVersion: 1, inputContractKey: 'bigdes-current-situation-evaluation-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        selectorSchema: {
            properties: { applicabilityId: 'tenant-owned-object-id' },
            required: ['applicabilityId'],
            additionalProperties: false,
        },
        sourcePageKey: 'bigdesCurrentSituationEvaluation',
        scopeModes: ['tenant'],
        outputFormats: ['pdf'],
        visibleFields: [
            'tenant_display_name', 'tenant_logo_url', 'generated_date', 'asset_group_name',
            'asset_group_no', 'category', 'category_label_tr', 'criticality_degree',
            'measure_code', 'measure_title', 'measure_implementation_status',
            'measure_effectiveness_status', 'criticality_level', 'audit_methods_label_tr',
            'auditor_comment',
        ],
        limits: { maxRecords: 5_000, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'overdue-actions': {
        key: 'overdue-actions', definitionVersion: 3,
        surfaceKey: 'incident-actions', domainKey: 'action',
        sourceSurfaces: { 'incident-actions': 'incidentActions', 'audit-compliance-capa': 'auditComplianceCapa' },
        presentation: { labelKey: 'reportCatalog.overdueActions', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_OVERDUE_ACTIONS', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'overdue-actions', moduleVersion: 1, inputContractKey: 'overdue-actions-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'simple-static-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'incidentActions', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['title', 'owner_name', 'deadline', 'delay_days', 'source_title', 'source_type', 'status', 'progress', 'action_type', 'action_narrative', 'start_date', 'budget_status'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'action-performance': {
        key: 'action-performance', definitionVersion: 3,
        surfaceKey: 'incident-actions', domainKey: 'action',
        sourceSurfaces: { 'incident-actions': 'incidentActions', 'audit-compliance-capa': 'auditComplianceCapa' },
        presentation: { labelKey: 'reportCatalog.actionPerformance', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ACTION_PERFORMANCE', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'action-performance', moduleVersion: 1, inputContractKey: 'action-performance-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'simple-static-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'incidentActions', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['title', 'unit_name', 'reassign_count', 'reject_count', 'duration_days', 'status', 'performance_commentary', 'pdca_status', 'deadline_variance_days'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'user-workload': {
        key: 'user-workload', definitionVersion: 2,
        surfaceKey: 'incident-actions', domainKey: 'action',
        sourceSurfaces: {
            'incident-actions': 'incidentActions',
            'audit-compliance-capa': 'auditComplianceCapa',
            'my-work': 'incidentActions',
        },
        presentation: { labelKey: 'reportCatalog.userWorkload', order: 20 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_USER_WORKLOAD', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'user-workload', moduleVersion: 1, inputContractKey: 'user-workload-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'simple-static-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'incidentActions', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['user_name', 'open_count', 'completed_count', 'overdue_count', 'total_count'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
    'vendor-detailed-portfolio': {
        key: 'vendor-detailed-portfolio', definitionVersion: 3,
        surfaceKey: 'vendor-register', domainKey: 'vendor',
        presentation: { labelKey: 'reportCatalog.vendorPortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_VENDOR_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'vendor-detailed-portfolio', moduleVersion: 1, inputContractKey: 'vendor-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'controlled-recipient-v1' },
        sourcePageKey: 'vendorManagementVendors', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['name', 'type', 'category', 'criticality', 'status', 'city', 'businessOwner', 'securityOwner', 'contractExpiration', 'nextAssessmentReviewDate', 'linkedAssetCount', 'description'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: true },
    },
    'vendor-record-dossier': {
        key: 'vendor-record-dossier', definitionVersion: 3,
        surfaceKey: 'vendor-detail', domainKey: 'vendor',
        presentation: { labelKey: 'reportCatalog.vendorDossier', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_VENDOR_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'vendor-record-dossier', moduleVersion: 1, inputContractKey: 'vendor-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'controlled-recipient-v1' },
        selectorSchema: { properties: { recordId: 'tenant-owned-object-id' }, required: ['recordId'], additionalProperties: false },
        sourcePageKey: 'vendorManagementVendors', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['name', 'type', 'category', 'criticality', 'status', 'city', 'businessOwner', 'securityOwner', 'contractExpiration', 'nextAssessmentReviewDate', 'contractSummary', 'bcmSummary', 'linkedAssets', 'description'],
        limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: true },
    },
    'incident-detailed-portfolio': {
        key: 'incident-detailed-portfolio', definitionVersion: 3,
        surfaceKey: 'incident-register', domainKey: 'incident',
        presentation: { labelKey: 'reportCatalog.incidentPortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_INCIDENT_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'incident-detailed-portfolio', moduleVersion: 1, inputContractKey: 'incident-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'controlled-recipient-v1' },
        sourcePageKey: 'incidentManagementIncidents', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['code', 'title', 'type', 'category', 'severity', 'impact', 'urgency', 'status', 'dates', 'slaState', 'primaryUnit', 'assignee', 'affectedCount', 'description', 'escalationLevel', 'dataBreachInvolved', 'regulatoryReporting'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: true },
    },
    'incident-record-dossier': {
        key: 'incident-record-dossier', definitionVersion: 3,
        surfaceKey: 'incident-detail', domainKey: 'incident',
        presentation: { labelKey: 'reportCatalog.incidentDossier', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_INCIDENT_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'incident-record-dossier', moduleVersion: 1, inputContractKey: 'incident-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'controlled-recipient-v1' },
        selectorSchema: { properties: { recordId: 'tenant-owned-object-id' }, required: ['recordId'], additionalProperties: false },
        sourcePageKey: 'incidentManagementIncidents', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['code', 'title', 'type', 'category', 'severity', 'impact', 'urgency', 'status', 'dates', 'slaState', 'primaryUnit', 'assignee', 'affectedLabels', 'rootCauseSummary', 'responseSummary', 'evidenceMetadata', 'description', 'impactDescription', 'escalationLevel', 'dataBreachInvolved', 'regulatoryReporting', 'linkedAssets', 'risks', 'controls', 'actions', 'problems', 'changes', 'tickets'],
        limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: true },
    },
    'bia-detailed-portfolio': {
        key: 'bia-detailed-portfolio', definitionVersion: 3,
        surfaceKey: 'bia-register', domainKey: 'bia',
        presentation: { labelKey: 'reportCatalog.biaPortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIA_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bia-detailed-portfolio', moduleVersion: 1, inputContractKey: 'bia-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'bcmBia', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['biaId', 'name', 'process', 'unit', 'location', 'criticality', 'state', 'rto', 'rpo', 'mtpd', 'impact', 'review', 'dependencyCounts', 'description', 'category', 'minimumServiceLevel', 'downtimeCost'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'bia-record-dossier': {
        key: 'bia-record-dossier', definitionVersion: 3,
        surfaceKey: 'bia-detail', domainKey: 'bia',
        presentation: { labelKey: 'reportCatalog.biaDossier', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIA_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bia-record-dossier', moduleVersion: 1, inputContractKey: 'bia-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'bcmBia', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['biaId', 'name', 'process', 'unit', 'location', 'criticality', 'state', 'rto', 'rpo', 'mtpd', 'impactMatrix', 'dependencies', 'resources', 'documentMetadata', 'description', 'category', 'minimumServiceLevel', 'downtimeCost', 'continuityRecoveryNotes', 'impact', 'review', 'dependencyCounts'],
        limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'bcp-detailed-portfolio': {
        key: 'bcp-detailed-portfolio', definitionVersion: 3,
        surfaceKey: 'bcp-register', domainKey: 'bcp',
        presentation: { labelKey: 'reportCatalog.bcpPortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BCP_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bcp-detailed-portfolio', moduleVersion: 1, inputContractKey: 'bcp-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'bcmBcp', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['code', 'name', 'state', 'status', 'process', 'unit', 'location', 'review', 'description', 'reviewSchedule'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'bcp-record-dossier': {
        key: 'bcp-record-dossier', definitionVersion: 3,
        surfaceKey: 'bcp-detail', domainKey: 'bcp',
        presentation: { labelKey: 'reportCatalog.bcpDossier', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BCP_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'bcp-record-dossier', moduleVersion: 1, inputContractKey: 'bcp-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'bcmBcp', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['code', 'name', 'state', 'status', 'bia', 'process', 'unit', 'location', 'author', 'reviewer', 'approver', 'review', 'recovery', 'roles', 'contactLocationMetadata', 'documentNames', 'exerciseLabels', 'description', 'reviewSchedule', 'activationCriteria', 'activationProcedure', 'deactivationProcedure', 'requiredResources', 'alternateLocation', 'communicationPlans'],
        limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'exercise-detailed-portfolio': {
        key: 'exercise-detailed-portfolio', definitionVersion: 3,
        surfaceKey: 'exercise-register', domainKey: 'exercise',
        presentation: { labelKey: 'reportCatalog.exercisePortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_EXERCISE_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'exercise-detailed-portfolio', moduleVersion: 1, inputContractKey: 'exercise-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'bcmExercises', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['code', 'name', 'type', 'status', 'dates', 'duration', 'location', 'objectiveSummary', 'resultSummary', 'counts', 'score', 'issues', 'description', 'scenario'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'exercise-record-dossier': {
        key: 'exercise-record-dossier', definitionVersion: 3,
        surfaceKey: 'exercise-detail', domainKey: 'exercise',
        presentation: { labelKey: 'reportCatalog.exerciseDossier', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_EXERCISE_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'exercise-record-dossier', moduleVersion: 1, inputContractKey: 'exercise-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'bcmExercises', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['code', 'name', 'type', 'status', 'bcpLabels', 'dates', 'duration', 'location', 'objectiveSummary', 'resultSummary', 'counts', 'score', 'issues', 'evaluations', 'improvements', 'documentNames', 'description', 'scenario', 'scenarioDevelopment', 'otherRemarks', 'facilitator', 'approvalSummary', 'participants', 'participatingVendors'],
        limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'gdpr-consent-detailed-portfolio': {
        key: 'gdpr-consent-detailed-portfolio', definitionVersion: 2,
        surfaceKey: 'gdpr-consent-register', domainKey: 'gdpr-consent',
        presentation: { labelKey: 'reportCatalog.gdprConsentPortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_GDPR_CONSENT_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'gdpr-consent-detailed-portfolio', moduleVersion: 1, inputContractKey: 'gdpr-consent-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'gdprObtainingExplicitConsent', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['pseudonymousRowKey', 'method', 'status', 'disclosureSignedAt', 'consentSignedAt', 'validCount', 'withdrawnCount', 'expiredCount'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'gdpr-consent-record-dossier': {
        key: 'gdpr-consent-record-dossier', definitionVersion: 2,
        surfaceKey: 'gdpr-consent-detail', domainKey: 'gdpr-consent',
        presentation: { labelKey: 'reportCatalog.gdprConsentDossier', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_GDPR_CONSENT_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'gdpr-consent-record-dossier', moduleVersion: 1, inputContractKey: 'gdpr-consent-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'gdprObtainingExplicitConsent', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['pseudonymousRowKey', 'method', 'status', 'disclosureSignedAt', 'consentSignedAt', 'disclosureDocumentPresent', 'consentDocumentPresent', 'consentAuditFacts'],
        limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'gdpr-dsr-detailed-portfolio': {
        key: 'gdpr-dsr-detailed-portfolio', definitionVersion: 2,
        surfaceKey: 'gdpr-dsr-register', domainKey: 'gdpr-dsr',
        presentation: { labelKey: 'reportCatalog.gdprDsrPortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_GDPR_DSR_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'gdpr-dsr-detailed-portfolio', moduleVersion: 1, inputContractKey: 'gdpr-dsr-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'gdprDataSubjectRequest', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['referenceNumber', 'requestType', 'status', 'receivedAt', 'deadline', 'respondedAt', 'unit', 'deadlineState'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'gdpr-dsr-record-dossier': {
        key: 'gdpr-dsr-record-dossier', definitionVersion: 2,
        surfaceKey: 'gdpr-dsr-register', domainKey: 'gdpr-dsr',
        presentation: { labelKey: 'reportCatalog.gdprDsrDossier', order: 20 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_GDPR_DSR_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'gdpr-dsr-record-dossier', moduleVersion: 1, inputContractKey: 'gdpr-dsr-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'gdprDataSubjectRequest', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['referenceNumber', 'requestType', 'status', 'receivedAt', 'deadline', 'respondedAt', 'unit', 'deadlineState', 'subjectSummary', 'descriptionSummary', 'responseSummary', 'attachmentCount', 'attachmentClasses', 'notesCount'],
        limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'gdpr-breach-detailed-portfolio': {
        key: 'gdpr-breach-detailed-portfolio', definitionVersion: 2,
        surfaceKey: 'gdpr-breach-register', domainKey: 'gdpr-breach',
        presentation: { labelKey: 'reportCatalog.gdprBreachPortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_GDPR_BREACH_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'gdpr-breach-detailed-portfolio', moduleVersion: 1, inputContractKey: 'gdpr-breach-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'gdprDataBreach', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['referenceNumber', 'redactedTitle', 'status', 'severity', 'detectedAt', 'affectedCount', 'confidentialityImpact', 'integrityImpact', 'availabilityImpact', 'notificationState', 'notificationDate'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'gdpr-breach-record-dossier': {
        key: 'gdpr-breach-record-dossier', definitionVersion: 2,
        surfaceKey: 'gdpr-breach-register', domainKey: 'gdpr-breach',
        presentation: { labelKey: 'reportCatalog.gdprBreachDossier', order: 20 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_GDPR_BREACH_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'gdpr-breach-record-dossier', moduleVersion: 1, inputContractKey: 'gdpr-breach-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'gdprDataBreach', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['referenceNumber', 'redactedTitle', 'status', 'severity', 'detectedAt', 'affectedCount', 'confidentialityImpact', 'integrityImpact', 'availabilityImpact', 'notificationState', 'notificationDate', 'categoryTypeAggregates', 'causeSummary', 'containmentSummary', 'remediationSummary', 'preventionSummary'],
        limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'ticket-detailed-portfolio': {
        key: 'ticket-detailed-portfolio', definitionVersion: 2,
        surfaceKey: 'ticket-register', domainKey: 'ticket',
        presentation: { labelKey: 'reportCatalog.ticketPortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_TICKET_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'ticket-detailed-portfolio', moduleVersion: 1, inputContractKey: 'ticket-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'ticketing', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['ticketNumber', 'mode', 'source', 'status', 'priority', 'assignmentState', 'dates', 'taskCount', 'commentCount', 'attachmentCount'],
        limits: { maxRecords: 25, maxSelectedRecords: 25, maxPayloadBytes: 1_048_576 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'ticket-record-dossier': {
        key: 'ticket-record-dossier', definitionVersion: 2,
        surfaceKey: 'ticket-detail', domainKey: 'ticket',
        presentation: { labelKey: 'reportCatalog.ticketDossier', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_TICKET_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'ticket-record-dossier', moduleVersion: 1, inputContractKey: 'ticket-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'ticketing', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['ticketNumber', 'mode', 'source', 'status', 'priority', 'assignmentState', 'dates', 'taskCount', 'commentCount', 'attachmentCount'],
        limits: { maxRecords: 1, maxPayloadBytes: 1_048_576 }, archiveType: null,
        capabilities: { profile: true, interactive: true, archive: true },
    },
    'risk-detailed-portfolio': {
        key: 'risk-detailed-portfolio', definitionVersion: 2,
        surfaceKey: 'risk-register', domainKey: 'risk',
        presentation: { labelKey: 'reportCatalog.riskPortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_RISK_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'risk-detailed-portfolio', moduleVersion: 1, inputContractKey: 'risk-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'portfolio-sections-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'controlled-recipient-v1' },
        sourcePageKey: 'riskOperationsRiskRegister', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['owner', 'inherent', 'residual', 'response', 'description', 'responsePlan', 'causes', 'consequences', 'controls', 'treatments', 'assets'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000 }, archiveType: null,
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: true },
    },
    'asset-detailed-portfolio': {
        key: 'asset-detailed-portfolio', definitionVersion: 2,
        surfaceKey: 'asset-register', domainKey: 'asset',
        presentation: { labelKey: 'reportCatalog.assetPortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ASSET_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'asset-detailed-portfolio', moduleVersion: 1, inputContractKey: 'asset-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'portfolio-sections-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'controlled-recipient-v1' },
        sourcePageKey: 'assetInventoryAssets', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['owner', 'type', 'category', 'group', 'cia', 'status', 'supportEnd', 'nextReview', 'definition', 'details', 'businessImpact', 'risks', 'relationshipTypes', 'threats', 'controls'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000 }, archiveType: null,
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: true },
    },
    'risk-record-dossier': {
        key: 'risk-record-dossier', definitionVersion: 2,
        surfaceKey: 'risk-detail', domainKey: 'risk',
        presentation: { labelKey: 'reportCatalog.riskDossier', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_RISK_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'risk-record-dossier', moduleVersion: 1, inputContractKey: 'risk-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'dossier-sections-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'controlled-recipient-v1' },
        selectorSchema: { properties: { recordId: 'tenant-owned-object-id' }, required: ['recordId'], additionalProperties: false },
        sourcePageKey: 'riskOperationsRiskRegister', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['recordType', 'priority', 'owners', 'probability', 'impact', 'response', 'createdAt', 'updatedAt', 'description', 'responsePlan', 'operationalImpact', 'financialImpactRange', 'reputationalImpact', 'causes', 'consequences', 'controls', 'treatments', 'assetCategories', 'processes', 'assets'],
        limits: { maxRecords: 1 }, archiveType: null,
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: true },
    },
    'asset-record-dossier': {
        key: 'asset-record-dossier', definitionVersion: 2,
        surfaceKey: 'asset-detail', domainKey: 'asset',
        presentation: { labelKey: 'reportCatalog.assetDossier', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ASSET_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'asset-record-dossier', moduleVersion: 1, inputContractKey: 'asset-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'dossier-sections-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'controlled-recipient-v1' },
        selectorSchema: { properties: { recordId: 'tenant-owned-object-id' }, required: ['recordId'], additionalProperties: false },
        sourcePageKey: 'assetInventoryAssets', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['owners', 'type', 'category', 'group', 'classification', 'criticality', 'location', 'workflow', 'supportEnd', 'nextReview', 'recoveryTargets', 'currentValue', 'definition', 'details', 'businessImpact', 'dependencies', 'manualWorkarounds', 'personalData', 'risks', 'threats', 'scfControls', 'assetRelationships', 'vendors', 'operationalControls', 'personalDataInventories', 'licenses', 'disposals', 'lifecycle'],
        limits: { maxRecords: 1 }, archiveType: null,
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: true },
    },
    'meeting-record-dossier': {
        key: 'meeting-record-dossier',
        definitionVersion: 2,
        surfaceKey: 'meeting-register',
        domainKey: 'meeting',
        presentation: { labelKey: 'reportCatalog.meetingDossier', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_MEETING_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'meeting-record-dossier', moduleVersion: 1, inputContractKey: 'meeting-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'dossier-sections-v1', schemaVersion: 1, optional: false },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'controlled-recipient-v1' },
        selectorSchema: { properties: { recordId: 'tenant-owned-object-id' }, required: ['recordId'], additionalProperties: false },
        sourcePageKey: 'isoMeeting',
        scopeModes: ['single'],
        outputFormats: ['pdf', 'docx'],
        visibleFields: ['title', 'code', 'date', 'type', 'status', 'location', 'participants', 'additionalParticipants', 'organizer', 'attendees', 'isManagementReview', 'agenda', 'additionalAgenda', 'topicsDiscussed', 'decisions', 'minutes', 'reviewScope', 'reviewInputs', 'previousReviewActions', 'description', 'createdAt', 'updatedAt'],
        limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: false },
    },
    'asset-disposal-list': registerTableDefinition({ key: 'asset-disposal-list', definitionVersion: 3, surfaceKey: 'asset-disposal', domainKey: 'asset', labelKey: 'reportCatalog.assetDisposalList', sourcePageKey: 'assetInventoryDisposalManagement', visibleFields: ['title', 'kind', 'status', 'destructionMethod', 'plannedDate', 'completedAt', 'certificateId', 'linkedAssetCount', 'createdAt', 'responsibleUser', 'notes'] }),
    'change-request-list': registerTableDefinition({ key: 'change-request-list', definitionVersion: 3, surfaceKey: 'change-request', domainKey: 'itsm', labelKey: 'reportCatalog.changeRequestList', sourcePageKey: 'itsmChangeRequest', visibleFields: ['title', 'changeType', 'status', 'approvalStatus', 'plannedWindowStart', 'plannedWindowEnd', 'riskSummary', 'createdAt', 'description'] }),
    'vendor-assessment-list': registerTableDefinition({ key: 'vendor-assessment-list', surfaceKey: 'vendor-assessments', domainKey: 'vendor', labelKey: 'reportCatalog.vendorAssessmentList', sourcePageKey: 'vendorManagementAssessments', visibleFields: ['title', 'vendor', 'assessmentDate', 'nextReviewDate', 'overallScore', 'status', 'findingsSummary', 'createdAt'] }),
    'personal-data-inventory-portfolio': registerTableDefinition({ key: 'personal-data-inventory-portfolio', definitionVersion: 3, surfaceKey: 'personal-data-inventory', domainKey: 'personal-data', labelKey: 'reportCatalog.personalDataInventoryPortfolio', sourcePageKey: 'assetInventoryPdInventory', order: 10, visibleFields: ['activity', 'activityCode', 'personalData', 'processingPurpose', 'subjectPersonGroup', 'purchaserGroup', 'collectionMethod', 'legalCauses', 'storageEnvironment', 'retentionDuration', 'status', 'createdAt', 'unit', 'personalDataLocation', 'personalDataSource', 'retentionDetails', 'internationalTransfer', 'transferDetails', 'securityMeasureCount', 'securityProfile', 'explicitConsentObligation', 'disclosureObligation', 'degreeOfConfidentiality', 'description'] }),
    'user-management-list': registerTableDefinition({ key: 'user-management-list', surfaceKey: 'user-management', domainKey: 'user', labelKey: 'reportCatalog.userManagementList', sourcePageKey: 'userOperationsUserManagement', visibleFields: ['fullName', 'userName', 'email', 'title', 'status', 'unit', 'roles', 'authSource', 'joinedAt', 'createdAt'] }),
    'risk-treatment-list': registerTableDefinition({ key: 'risk-treatment-list', definitionVersion: 3, surfaceKey: 'risk-treatment', domainKey: 'risk', labelKey: 'reportCatalog.riskTreatmentList', sourcePageKey: 'riskOperationsTreatment', visibleFields: ['title', 'strategy', 'status', 'progress', 'budget', 'budgetStatus', 'unit', 'startDate', 'deadline', 'createdAt', 'owners', 'treatmentNarrative'] }),
    'service-catalog-list': registerTableDefinition({ key: 'service-catalog-list', surfaceKey: 'service-catalog', domainKey: 'itsm', labelKey: 'reportCatalog.serviceCatalogList', sourcePageKey: 'itsmServiceCatalog', visibleFields: ['name', 'code', 'serviceType', 'status', 'ownerName', 'description', 'createdAt'] }),
    'process-list': registerTableDefinition({ key: 'process-list', definitionVersion: 3, surfaceKey: 'organization-process', domainKey: 'organization', labelKey: 'reportCatalog.processList', sourcePageKey: 'definitionsGeneralSystemProcess', visibleFields: ['name', 'code', 'criticality', 'rto', 'rpo', 'mtpd', 'description', 'createdAt', 'unit', 'processOwner'] }),
    'competence-list': registerTableDefinition({ key: 'competence-list', definitionVersion: 3, surfaceKey: 'competence', domainKey: 'training', labelKey: 'reportCatalog.competenceList', sourcePageKey: 'isoCompetence', visibleFields: ['name', 'description', 'level', 'numberOfPersonnel', 'createdAt', 'status'] }),
    'training-list': registerTableDefinition({ key: 'training-list', definitionVersion: 3, surfaceKey: 'training', domainKey: 'training', labelKey: 'reportCatalog.trainingList', sourcePageKey: 'isoTraining', visibleFields: ['title', 'code', 'type', 'category', 'duration', 'mandatory', 'scheduledDate', 'deadlineDate', 'status', 'createdAt', 'description', 'instructor', 'trainedPersons', 'competencesGained'] }),
    'retention-period-list': registerTableDefinition({ key: 'retention-period-list', surfaceKey: 'retention-periods', domainKey: 'personal-data', labelKey: 'reportCatalog.retentionPeriodList', sourcePageKey: 'kvkkRetentionPeriod', visibleFields: ['name', 'code', 'duration', 'durationUnit', 'legalReference', 'status', 'createdAt'] }),
    'asset-license-list': registerTableDefinition({ key: 'asset-license-list', surfaceKey: 'asset-license', domainKey: 'asset', labelKey: 'reportCatalog.assetLicenseList', sourcePageKey: 'assetInventoryLicenseManagement', visibleFields: ['name', 'vendor', 'licenseType', 'seatCount', 'startDate', 'endDate', 'status', 'cost', 'currency', 'renewalType', 'autoRenew', 'createdAt'] }),
    'role-management-list': registerTableDefinition({ key: 'role-management-list', surfaceKey: 'role-management', domainKey: 'user', labelKey: 'reportCatalog.roleManagementList', sourcePageKey: 'userOperationsRoleManagement', visibleFields: ['name', 'description', 'isDefault', 'isSystemRole', 'unitCount', 'assignmentCount', 'createdAt'] }),
    'regulatory-source-list': registerTableDefinition({ key: 'regulatory-source-list', surfaceKey: 'regulatory-sources', domainKey: 'compliance', labelKey: 'reportCatalog.regulatorySourceList', sourcePageKey: 'definitionsComplianceRegulatorySources', visibleFields: ['name', 'shortCode', 'kind', 'issuer', 'effectiveFrom', 'effectiveTo', 'status', 'description', 'createdAt'] }),
    'bia-impact-scale-list': registerTableDefinition({ key: 'bia-impact-scale-list', surfaceKey: 'bia-impact-scale', domainKey: 'bia', labelKey: 'reportCatalog.biaImpactScaleList', sourcePageKey: 'definitionsScaleBiaImpact', scopeModes: ['tenant'], maxRecords: 250, visibleFields: ['section', 'item', 'label', 'value', 'unit', 'details'] }),
    'bia-recovery-resources': registerTableDefinition({ key: 'bia-recovery-resources', definitionVersion: 3, surfaceKey: 'bia-recovery-resources', domainKey: 'bia', labelKey: 'reportCatalog.biaRecoveryResources', sourcePageKey: 'bcmBia', scopeModes: ['tenant'], selectorSchema: BIA_RELATION_SELECTOR_SCHEMA, visibleFields: ['name', 'type', 'criticality', 'normalLevel', 'req2h', 'req8h', 'req24h', 'req2d', 'req1w', 'availability', 'gap', 'status', 'details', 'reqOther', 'owner', 'contingency', 'linkedProcess'] }),
    'bia-related-processes': registerTableDefinition({ key: 'bia-related-processes', surfaceKey: 'bia-related-processes', domainKey: 'bia', labelKey: 'reportCatalog.biaRelatedProcesses', sourcePageKey: 'bcmBia', scopeModes: ['tenant'], selectorSchema: BIA_RELATION_SELECTOR_SCHEMA, visibleFields: ['name', 'description', 'unit', 'rto', 'rpo', 'mtpd', 'criticality', 'processOwner'] }),
    'bia-related-assets': registerTableDefinition({ key: 'bia-related-assets', surfaceKey: 'bia-related-assets', domainKey: 'bia', labelKey: 'reportCatalog.biaRelatedAssets', sourcePageKey: 'bcmBia', scopeModes: ['tenant'], selectorSchema: BIA_RELATION_SELECTOR_SCHEMA, visibleFields: ['assetId', 'assetName', 'category', 'assetGroup', 'location', 'classificationLevel', 'institutionApproval', 'usingStatus', 'purchaseDate', 'purchasePrice', 'currentValue', 'depreciation', 'expectedLifespan'] }),
    'bia-related-vendors': registerTableDefinition({ key: 'bia-related-vendors', surfaceKey: 'bia-related-vendors', domainKey: 'bia', labelKey: 'reportCatalog.biaRelatedVendors', sourcePageKey: 'bcmBia', scopeModes: ['tenant'], selectorSchema: BIA_RELATION_SELECTOR_SCHEMA, visibleFields: ['name', 'type', 'category', 'city', 'status', 'criticality', 'vendorProductService', 'contractExpiration', 'vendorBusinessImpact', 'slaDetails', 'recoveryTimeObjective', 'alternateVendor', 'description'] }),
    'bia-related-bcps': registerTableDefinition({ key: 'bia-related-bcps', surfaceKey: 'bia-related-bcps', domainKey: 'bia', labelKey: 'reportCatalog.biaRelatedBcps', sourcePageKey: 'bcmBia', scopeModes: ['tenant'], selectorSchema: BIA_RELATION_SELECTOR_SCHEMA, visibleFields: ['name', 'code', 'status', 'state'] }),
    'bia-related-risks': registerTableDefinition({ key: 'bia-related-risks', surfaceKey: 'bia-related-risks', domainKey: 'bia', labelKey: 'reportCatalog.biaRelatedRisks', sourcePageKey: 'bcmBia', scopeModes: ['tenant'], selectorSchema: BIA_RELATION_SELECTOR_SCHEMA, visibleFields: ['riskId', 'title', 'riskLevel', 'score', 'unit', 'priority'] }),
    'dpia-assessment-list': registerTableDefinition({ key: 'dpia-assessment-list', definitionVersion: 3, surfaceKey: 'dpia-assessment', domainKey: 'personal-data', labelKey: 'reportCatalog.dpiaAssessmentList', sourcePageKey: 'gdprDpiaAssessment', visibleFields: ['code', 'title', 'assessmentType', 'language', 'screeningScore', 'dpiaRequired', 'outcome', 'residualRiskLevel', 'status', 'submittedAt', 'completedAt', 'linkedRiskCount', 'createdAt', 'description'] }),
    'privacy-notice-list': registerTableDefinition({ key: 'privacy-notice-list', definitionVersion: 3, surfaceKey: 'privacy-notice', domainKey: 'personal-data', labelKey: 'reportCatalog.privacyNoticeList', sourcePageKey: 'gdprPrivacyNotice', visibleFields: ['code', 'title', 'noticeType', 'channels', 'language', 'version', 'status', 'needsReview', 'effectiveDate', 'publishedAt', 'inventoryItemCount', 'createdAt', 'description', 'needsReviewReason'] }),
    'dpa-registry-list': registerTableDefinition({ key: 'dpa-registry-list', definitionVersion: 3, surfaceKey: 'dpa-registry', domainKey: 'personal-data', labelKey: 'reportCatalog.dpaRegistryList', sourcePageKey: 'gdprDpa', visibleFields: ['code', 'title', 'vendor', 'role', 'agreementType', 'art28Score', 'validFrom', 'validUntil', 'autoRenew', 'status', 'inventoryItemCount', 'subProcessorCount', 'createdAt', 'description'] }),
    'objective-list': registerTableDefinition({ key: 'objective-list', definitionVersion: 3, surfaceKey: 'objective', domainKey: 'isms-controls', labelKey: 'reportCatalog.objectiveList', sourcePageKey: 'isoObjective', visibleFields: ['objectiveName', 'code', 'unit', 'startDate', 'endDate', 'status', 'riskCount', 'treatmentCount', 'kpiCount', 'createdAt', 'description'] }),
    'threat-intelligence-list': registerTableDefinition({ key: 'threat-intelligence-list', definitionVersion: 3, surfaceKey: 'threat-intelligence', domainKey: 'risk', labelKey: 'reportCatalog.threatIntelligenceList', sourcePageKey: 'ismsControlsThreatIntelligence', visibleFields: ['threatName', 'threatID', 'category', 'source', 'likelihood', 'status', 'assetCount', 'controlCount', 'createdAt', 'description'] }),
    'vulnerability-list': registerTableDefinition({ key: 'vulnerability-list', definitionVersion: 3, surfaceKey: 'vulnerability', domainKey: 'risk', labelKey: 'reportCatalog.vulnerabilityList', sourcePageKey: 'ismsControlsVulnerability', visibleFields: ['title', 'identifier', 'severity', 'assetCount', 'createdAt', 'description'] }),
    'risk-opportunity-list': registerTableDefinition({ key: 'risk-opportunity-list', definitionVersion: 3, surfaceKey: 'risk-opportunities', domainKey: 'risk', labelKey: 'reportCatalog.riskOpportunityList', sourcePageKey: 'riskOperationsRiskOpportunities', visibleFields: ['title', 'riskId', 'priority', 'riskLevel', 'score', 'impact', 'probability', 'unit', 'responseStrategy', 'financialImpact', 'residualScore', 'createdAt', 'responseDescription', 'description'] }),
    'kpi-dashboard-list': registerTableDefinition({ key: 'kpi-dashboard-list', definitionVersion: 3, surfaceKey: 'kpi-dashboard', domainKey: 'audit', labelKey: 'reportCatalog.kpiDashboardList', sourcePageKey: 'auditComplianceKpiDashboard', visibleFields: ['name', 'category', 'targetValue', 'currentValue', 'unit', 'frequency', 'responsible', 'status', 'measurementCount', 'createdAt', 'formula', 'dataSource', 'description'] }),
    'context-list': registerTableDefinition({ key: 'context-list', definitionVersion: 3, surfaceKey: 'organizational-context', domainKey: 'isms-controls', labelKey: 'reportCatalog.contextList', sourcePageKey: 'isoContext', visibleFields: ['name', 'code', 'type', 'status', 'subItemCount', 'createdAt', 'organizationalPurpose', 'intendedOutcomes', 'description'] }),
    'interested-parties-list': registerTableDefinition({ key: 'interested-parties-list', definitionVersion: 3, surfaceKey: 'interested-parties', domainKey: 'isms-controls', labelKey: 'reportCatalog.interestedPartiesList', sourcePageKey: 'contextManagementInterestedParties', visibleFields: ['name', 'code', 'type', 'category', 'communicationMethod', 'communicationFrequency', 'contactPerson', 'contactEmail', 'status', 'createdAt', 'expectations', 'obligations'] }),
    'scope-document-list': registerTableDefinition({ key: 'scope-document-list', definitionVersion: 3, surfaceKey: 'scope-document', domainKey: 'isms-controls', labelKey: 'reportCatalog.scopeDocumentList', sourcePageKey: 'contextManagementScopeDocument', visibleFields: ['title', 'version', 'approvedBy', 'approvedDate', 'reviewDate', 'status', 'createdAt', 'geographicScope', 'organizationalScope', 'technologicalScope', 'exclusions', 'exclusionJustification'] }),
    'meeting-list': registerTableDefinition({ key: 'meeting-list', definitionVersion: 3, surfaceKey: 'meeting-register', domainKey: 'meeting', labelKey: 'reportCatalog.meetingList', sourcePageKey: 'isoMeeting', order: 0, visibleFields: ['title', 'code', 'date', 'participants', 'isManagementReview', 'type', 'status', 'location', 'createdAt', 'agenda', 'decisions', 'description'] }),
    'system-log-compliance': {
        key: 'system-log-compliance', definitionVersion: 2,
        surfaceKey: 'system-logs', domainKey: 'system-log',
        presentation: { labelKey: 'reportCatalog.systemLogCompliance', order: 10, menuVisibility: 'direct' },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_SYSTEM_LOG_COMPLIANCE', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'technical-ops',
        renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
        composition: { moduleKey: 'system-log-compliance', moduleVersion: 1, inputContractKey: 'system-log-compliance-input', inputContractVersion: 1, documentSpecVersion: 1 },
        profile: { schemaKey: 'simple-static-table-v1', schemaVersion: 1, optional: true },
        artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
        sourcePageKey: 'userOperationsSystemLogs', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf'],
        visibleFields: ['timestamp', 'actor', 'action', 'category', 'severity', 'resource', 'result', 'ip', 'requestReference', 'sessionReference'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 1_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true, archive: true },
    },
};

export const REPORT_CATALOG = validateReportCatalog(rawReportCatalog);
export const REPORT_KEYS = Object.freeze(Object.keys(REPORT_CATALOG));

// Historical definitions stay explicit and immutable. When a definition version is
// advanced, its previous definition must move here until every pinned job, snapshot,
// API and renderer in the N/N-1 deployment window has drained.
const rawHistoricalReportDefinitions = [
    {
      key: 'risk-filtered',
      definitionVersion: 2,
      surfaceKey: 'risk-register',
      domainKey: 'risk',
      presentation: { labelKey: 'reportCatalog.riskFiltered', order: 0, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_RISK_FILTERED', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'generic-list',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'risk-filtered', moduleVersion: 1, inputContractKey: 'risk-filtered-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'riskOperationsRiskRegister',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx', 'xlsx' ],
      visibleFields: [ 'riskId', 'riskNature', 'title', 'priority', 'probability', 'impact', 'score', 'riskLevel', 'status', 'responseStrategy', 'owners' ],
      limits: { maxRecords: 25000, maxSelectedRecords: 5000, maxPayloadBytes: 10485760 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'asset-filtered',
      definitionVersion: 2,
      surfaceKey: 'asset-register',
      domainKey: 'asset',
      presentation: { labelKey: 'reportCatalog.assetFiltered', order: 0, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ASSET_FILTERED', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'generic-list',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'asset-filtered', moduleVersion: 1, inputContractKey: 'asset-filtered-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'assetInventoryAssets',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx', 'xlsx' ],
      visibleFields: [
        'assetId',
        'assetName',
        'category',
        'assetCategory',
        'assetGroup',
        'classificationLevel',
        'usingStatus',
        'owners',
        'location',
        'purchaseDate',
        'currentValue',
        'currency'
      ],
      limits: { maxRecords: 25000, maxSelectedRecords: 5000, maxPayloadBytes: 10485760 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'audit-findings',
      definitionVersion: 2,
      surfaceKey: 'audit-assessment',
      domainKey: 'audit',
      presentation: { labelKey: 'reportCatalog.auditFindings', order: 0, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_AUDIT_FINDINGS', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'statutory-template',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'audit-findings', moduleVersion: 1, inputContractKey: 'audit-findings-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'simple-static-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'auditFinding',
      scopeModes: [ 'tenant' ],
      outputFormats: [ 'pdf' ],
      visibleFields: [ 'name', 'control_name', 'severity', 'status', 'is_overdue', 'overdue_label', 'assignee', 'deadline' ],
      limits: { maxRecords: 2000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'document-filtered',
      definitionVersion: 2,
      surfaceKey: 'report-document',
      domainKey: 'document',
      presentation: { labelKey: 'reportCatalog.documentFiltered', order: 20, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_DOCUMENT_FILTERED', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'generic-list',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'document-filtered', moduleVersion: 1, inputContractKey: 'document-filtered-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'reportDocument',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx', 'xlsx' ],
      visibleFields: [ 'code', 'title', 'type', 'classification', 'unit', 'status', 'workflow', 'review', 'description', 'documentMetadata', 'createdAt', 'updatedAt' ],
      limits: { maxRecords: 5000, maxSelectedRecords: 5000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'document-detailed-portfolio',
      definitionVersion: 2,
      surfaceKey: 'report-document',
      domainKey: 'document',
      presentation: { labelKey: 'reportCatalog.documentPortfolio', order: 30, menuVisibility: 'configuration-only' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_DOCUMENT_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'portfolio',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'document-detailed-portfolio', moduleVersion: 1, inputContractKey: 'document-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'reportDocument',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx' ],
      visibleFields: [ 'code', 'title', 'type', 'classification', 'unit', 'status', 'workflow', 'review', 'description', 'documentMetadata', 'createdAt', 'updatedAt' ],
      limits: { maxRecords: 5000, maxSelectedRecords: 5000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: false, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'soa',
      definitionVersion: 3,
      surfaceKey: 'isms-controls-soa',
      domainKey: 'isms-controls',
      presentation: { labelKey: 'reportCatalog.soa', order: 0, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_SOA', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'statutory-template',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'soa', moduleVersion: 1, inputContractKey: 'soa-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'grouped-statutory-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'ismsControlsSoa',
      scopeModes: [ 'tenant' ],
      outputFormats: [ 'pdf' ],
      visibleFields: [ 'control_no', 'control_name', 'domain_identifier', 'tenant_overview_status', 'maturity_line', 'objective_summary', 'related_documents_text', 'comment' ],
      limits: { maxRecords: 2000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'overdue-actions',
      definitionVersion: 2,
      surfaceKey: 'incident-actions',
      domainKey: 'action',
      presentation: { labelKey: 'reportCatalog.overdueActions', order: 0, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_OVERDUE_ACTIONS', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'statutory-template',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'overdue-actions', moduleVersion: 1, inputContractKey: 'overdue-actions-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'simple-static-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'incidentActions',
      sourceSurfaces: { 'incident-actions': 'incidentActions', 'audit-compliance-capa': 'auditComplianceCapa' },
      scopeModes: [ 'tenant' ],
      outputFormats: [ 'pdf' ],
      visibleFields: [ 'title', 'owner_name', 'deadline', 'delay_days', 'source_title', 'source_type', 'status', 'progress', 'action_type' ],
      limits: { maxRecords: 2000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'action-performance',
      definitionVersion: 2,
      surfaceKey: 'incident-actions',
      domainKey: 'action',
      presentation: { labelKey: 'reportCatalog.actionPerformance', order: 10, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ACTION_PERFORMANCE', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'statutory-template',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'action-performance', moduleVersion: 1, inputContractKey: 'action-performance-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'simple-static-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'incidentActions',
      sourceSurfaces: { 'incident-actions': 'incidentActions', 'audit-compliance-capa': 'auditComplianceCapa' },
      scopeModes: [ 'tenant' ],
      outputFormats: [ 'pdf' ],
      visibleFields: [ 'title', 'unit_name', 'reassign_count', 'reject_count', 'duration_days', 'status' ],
      limits: { maxRecords: 2000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'vendor-detailed-portfolio',
      definitionVersion: 2,
      surfaceKey: 'vendor-register',
      domainKey: 'vendor',
      presentation: { labelKey: 'reportCatalog.vendorPortfolio', order: 10, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_VENDOR_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'portfolio',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'vendor-detailed-portfolio', moduleVersion: 1, inputContractKey: 'vendor-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'vendorManagementVendors',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx' ],
      visibleFields: [ 'name', 'type', 'category', 'criticality', 'status', 'city', 'businessOwner', 'securityOwner', 'contractExpiration', 'nextAssessmentReviewDate', 'linkedAssetCount' ],
      limits: { maxRecords: 5000, maxSelectedRecords: 5000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: false, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'vendor-record-dossier',
      definitionVersion: 2,
      surfaceKey: 'vendor-detail',
      domainKey: 'vendor',
      presentation: { labelKey: 'reportCatalog.vendorDossier', order: 10, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_VENDOR_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'dossier',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'vendor-record-dossier', moduleVersion: 1, inputContractKey: 'vendor-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'vendorManagementVendors',
      scopeModes: [ 'single' ],
      outputFormats: [ 'pdf', 'docx' ],
      visibleFields: [
        'name',
        'type',
        'category',
        'criticality',
        'status',
        'city',
        'businessOwner',
        'securityOwner',
        'contractExpiration',
        'nextAssessmentReviewDate',
        'contractSummary',
        'bcmSummary',
        'linkedAssets'
      ],
      limits: { maxRecords: 1, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: false, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'incident-detailed-portfolio',
      definitionVersion: 2,
      surfaceKey: 'incident-register',
      domainKey: 'incident',
      presentation: { labelKey: 'reportCatalog.incidentPortfolio', order: 10, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_INCIDENT_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'portfolio',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'incident-detailed-portfolio', moduleVersion: 1, inputContractKey: 'incident-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'incidentManagementIncidents',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx' ],
      visibleFields: [
        'code',          'title',
        'type',          'category',
        'severity',      'impact',
        'urgency',       'status',
        'dates',         'slaState',
        'primaryUnit',   'assignee',
        'affectedCount'
      ],
      limits: { maxRecords: 5000, maxSelectedRecords: 5000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: false, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'incident-record-dossier',
      definitionVersion: 2,
      surfaceKey: 'incident-detail',
      domainKey: 'incident',
      presentation: { labelKey: 'reportCatalog.incidentDossier', order: 10, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_INCIDENT_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'dossier',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'incident-record-dossier', moduleVersion: 1, inputContractKey: 'incident-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'incidentManagementIncidents',
      scopeModes: [ 'single' ],
      outputFormats: [ 'pdf', 'docx' ],
      visibleFields: [
        'code',            'title',
        'type',            'category',
        'severity',        'impact',
        'urgency',         'status',
        'dates',           'slaState',
        'primaryUnit',     'assignee',
        'affectedLabels',  'rootCauseSummary',
        'responseSummary', 'evidenceMetadata'
      ],
      limits: { maxRecords: 1, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: false, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'bia-detailed-portfolio',
      definitionVersion: 2,
      surfaceKey: 'bia-register',
      domainKey: 'bia',
      presentation: { labelKey: 'reportCatalog.biaPortfolio', order: 10, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIA_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'portfolio',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'bia-detailed-portfolio', moduleVersion: 1, inputContractKey: 'bia-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'bcmBia',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx' ],
      visibleFields: [
        'biaId',            'name',
        'process',          'unit',
        'location',         'criticality',
        'state',            'rto',
        'rpo',              'mtpd',
        'impact',           'review',
        'dependencyCounts'
      ],
      limits: { maxRecords: 5000, maxSelectedRecords: 5000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: false, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'bia-record-dossier',
      definitionVersion: 2,
      surfaceKey: 'bia-detail',
      domainKey: 'bia',
      presentation: { labelKey: 'reportCatalog.biaDossier', order: 10, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIA_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'dossier',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'bia-record-dossier', moduleVersion: 1, inputContractKey: 'bia-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'bcmBia',
      scopeModes: [ 'single' ],
      outputFormats: [ 'pdf', 'docx' ],
      visibleFields: [
        'biaId',        'name',
        'process',      'unit',
        'location',     'criticality',
        'state',        'rto',
        'rpo',          'mtpd',
        'impactMatrix', 'dependencies',
        'resources',    'documentMetadata'
      ],
      limits: { maxRecords: 1, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: false, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'bcp-detailed-portfolio',
      definitionVersion: 2,
      surfaceKey: 'bcp-register',
      domainKey: 'bcp',
      presentation: { labelKey: 'reportCatalog.bcpPortfolio', order: 10, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BCP_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'portfolio',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'bcp-detailed-portfolio', moduleVersion: 1, inputContractKey: 'bcp-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'bcmBcp',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx' ],
      visibleFields: [
        'code',     'name',
        'state',    'status',
        'process',  'unit',
        'location', 'review'
      ],
      limits: { maxRecords: 5000, maxSelectedRecords: 5000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: false, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'bcp-record-dossier',
      definitionVersion: 2,
      surfaceKey: 'bcp-detail',
      domainKey: 'bcp',
      presentation: { labelKey: 'reportCatalog.bcpDossier', order: 10, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BCP_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'dossier',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'bcp-record-dossier', moduleVersion: 1, inputContractKey: 'bcp-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'bcmBcp',
      scopeModes: [ 'single' ],
      outputFormats: [ 'pdf', 'docx' ],
      visibleFields: [
        'code',
        'name',
        'state',
        'status',
        'bia',
        'process',
        'unit',
        'location',
        'author',
        'reviewer',
        'approver',
        'review',
        'recovery',
        'roles',
        'contactLocationMetadata',
        'documentNames',
        'exerciseLabels'
      ],
      limits: { maxRecords: 1, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: false, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'exercise-detailed-portfolio',
      definitionVersion: 2,
      surfaceKey: 'exercise-register',
      domainKey: 'exercise',
      presentation: { labelKey: 'reportCatalog.exercisePortfolio', order: 10, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_EXERCISE_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'portfolio',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'exercise-detailed-portfolio', moduleVersion: 1, inputContractKey: 'exercise-detailed-portfolio-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'bcmExercises',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx' ],
      visibleFields: [ 'code', 'name', 'type', 'status', 'dates', 'duration', 'location', 'objectiveSummary', 'resultSummary', 'counts', 'score', 'issues' ],
      limits: { maxRecords: 5000, maxSelectedRecords: 5000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: false, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'exercise-record-dossier',
      definitionVersion: 2,
      surfaceKey: 'exercise-detail',
      domainKey: 'exercise',
      presentation: { labelKey: 'reportCatalog.exerciseDossier', order: 10, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_EXERCISE_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'dossier',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'exercise-record-dossier', moduleVersion: 1, inputContractKey: 'exercise-record-dossier-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'detailed-structured-v1', schemaVersion: 1, optional: false },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'bcmExercises',
      scopeModes: [ 'single' ],
      outputFormats: [ 'pdf', 'docx' ],
      visibleFields: [
        'code',             'name',
        'type',             'status',
        'bcpLabels',        'dates',
        'duration',         'location',
        'objectiveSummary', 'resultSummary',
        'counts',           'score',
        'issues',           'evaluations',
        'improvements',     'documentNames'
      ],
      limits: { maxRecords: 1, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: false, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'asset-disposal-list',
      definitionVersion: 2,
      surfaceKey: 'asset-disposal',
      domainKey: 'asset',
      presentation: { labelKey: 'reportCatalog.assetDisposalList', order: 0, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ASSET_DISPOSAL_LIST', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'generic-list',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'asset-disposal-list', moduleVersion: 1, inputContractKey: 'asset-disposal-list-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'assetInventoryDisposalManagement',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx', 'xlsx' ],
      visibleFields: [ 'title', 'kind', 'status', 'destructionMethod', 'plannedDate', 'completedAt', 'certificateId', 'linkedAssetCount', 'createdAt' ],
      limits: { maxRecords: 5000, maxSelectedRecords: 1000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'change-request-list',
      definitionVersion: 2,
      surfaceKey: 'change-request',
      domainKey: 'itsm',
      presentation: { labelKey: 'reportCatalog.changeRequestList', order: 0, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_CHANGE_REQUEST_LIST', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'generic-list',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'change-request-list', moduleVersion: 1, inputContractKey: 'change-request-list-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'itsmChangeRequest',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx', 'xlsx' ],
      visibleFields: [ 'title', 'changeType', 'status', 'approvalStatus', 'plannedWindowStart', 'plannedWindowEnd', 'riskSummary', 'createdAt' ],
      limits: { maxRecords: 5000, maxSelectedRecords: 1000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'personal-data-inventory-portfolio',
      definitionVersion: 2,
      surfaceKey: 'personal-data-inventory',
      domainKey: 'personal-data',
      presentation: { labelKey: 'reportCatalog.personalDataInventoryPortfolio', order: 10, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: {
        owner: 'reporting-platform',
        flag: 'REPORT_DEFINITION_PERSONAL_DATA_INVENTORY_PORTFOLIO',
        minimumCanaryRuns: 0,
        minimumCanaryDays: 0,
        maximumAuthorizationMismatches: 0
      },
      legacyCompatibility: 'none',
      reportClass: 'generic-list',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: {
        moduleKey: 'personal-data-inventory-portfolio',
        moduleVersion: 1,
        inputContractKey: 'personal-data-inventory-portfolio-input',
        inputContractVersion: 1,
        documentSpecVersion: 1
      },
      profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'assetInventoryPdInventory',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx', 'xlsx' ],
      visibleFields: [
        'activity',
        'activityCode',
        'personalData',
        'processingPurpose',
        'subjectPersonGroup',
        'purchaserGroup',
        'collectionMethod',
        'legalCauses',
        'storageEnvironment',
        'retentionDuration',
        'status',
        'createdAt'
      ],
      limits: { maxRecords: 5000, maxSelectedRecords: 1000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'risk-treatment-list',
      definitionVersion: 2,
      surfaceKey: 'risk-treatment',
      domainKey: 'risk',
      presentation: { labelKey: 'reportCatalog.riskTreatmentList', order: 0, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_RISK_TREATMENT_LIST', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'generic-list',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'risk-treatment-list', moduleVersion: 1, inputContractKey: 'risk-treatment-list-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'riskOperationsTreatment',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx', 'xlsx' ],
      visibleFields: [
        'title',    'strategy',
        'status',   'progress',
        'budget',   'budgetStatus',
        'unit',     'startDate',
        'deadline', 'createdAt'
      ],
      limits: { maxRecords: 5000, maxSelectedRecords: 1000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'process-list',
      definitionVersion: 2,
      surfaceKey: 'organization-process',
      domainKey: 'organization',
      presentation: { labelKey: 'reportCatalog.processList', order: 0, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_PROCESS_LIST', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'generic-list',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'process-list', moduleVersion: 1, inputContractKey: 'process-list-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'definitionsGeneralSystemProcess',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx', 'xlsx' ],
      visibleFields: [ 'name', 'code', 'criticality', 'rto', 'rpo', 'mtpd', 'description', 'createdAt' ],
      limits: { maxRecords: 5000, maxSelectedRecords: 1000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'competence-list',
      definitionVersion: 2,
      surfaceKey: 'competence',
      domainKey: 'training',
      presentation: { labelKey: 'reportCatalog.competenceList', order: 0, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_COMPETENCE_LIST', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'generic-list',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'competence-list', moduleVersion: 1, inputContractKey: 'competence-list-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'isoCompetence',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx', 'xlsx' ],
      visibleFields: [ 'name', 'description', 'level', 'numberOfPersonnel', 'createdAt' ],
      limits: { maxRecords: 5000, maxSelectedRecords: 1000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'training-list',
      definitionVersion: 2,
      surfaceKey: 'training',
      domainKey: 'training',
      presentation: { labelKey: 'reportCatalog.trainingList', order: 0, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_TRAINING_LIST', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'generic-list',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'training-list', moduleVersion: 1, inputContractKey: 'training-list-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: {}, required: [], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'isoTraining',
      scopeModes: [ 'filtered', 'selected' ],
      outputFormats: [ 'pdf', 'docx', 'xlsx' ],
      visibleFields: [ 'title', 'code', 'type', 'category', 'duration', 'mandatory', 'scheduledDate', 'deadlineDate', 'status', 'createdAt' ],
      limits: { maxRecords: 5000, maxSelectedRecords: 1000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
      key: 'bia-recovery-resources',
      definitionVersion: 2,
      surfaceKey: 'bia-recovery-resources',
      domainKey: 'bia',
      presentation: { labelKey: 'reportCatalog.biaRecoveryResources', order: 0, menuVisibility: 'direct' },
      lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: true, rolloutAudience: 'all' },
      rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIA_RECOVERY_RESOURCES', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
      legacyCompatibility: 'none',
      reportClass: 'generic-list',
      renderer: { key: 'programmatic-structured-v3', contractVersion: 3 },
      composition: { moduleKey: 'bia-recovery-resources', moduleVersion: 1, inputContractKey: 'bia-recovery-resources-input', inputContractVersion: 1, documentSpecVersion: 1 },
      profile: { schemaKey: 'register-table-v1', schemaVersion: 1, optional: true },
      artifact: { archiveEnabled: true, canonicalFormat: 'pdf', accessPolicy: 'source-current-v1' },
      selectorSchema: { properties: { biaId: 'tenant-owned-object-id' }, required: [ 'biaId' ], additionalProperties: false },
      selectorDefaults: {},
      sourcePageKey: 'bcmBia',
      scopeModes: [ 'tenant' ],
      outputFormats: [ 'pdf', 'docx', 'xlsx' ],
      visibleFields: [
        'name',        'type',
        'criticality', 'normalLevel',
        'req2h',       'req8h',
        'req24h',      'req2d',
        'req1w',       'availability',
        'gap',         'status',
        'details'
      ],
      limits: { maxRecords: 5000, maxPayloadBytes: 2097152 },
      archiveType: null,
      capabilities: { profile: true, profileOptional: true, interactive: true, schedule: false, archive: true, snapshot: false, compare: false, aiNarrative: false, designer: false }
    },
    {
        key: 'personal-data-inventory-portfolio', definitionVersion: 1,
        surfaceKey: 'personal-data-inventory', domainKey: 'personal-data',
        presentation: { labelKey: 'reportCatalog.personalDataInventoryPortfolio', order: 10 },
        lifecycle: { runEnabled: false, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'internal' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_PERSONAL_DATA_INVENTORY_PORTFOLIO', minimumCanaryRuns: 25, minimumCanaryDays: 3, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
        sourcePageKey: 'assetInventoryPdInventory', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf'],
        visibleFields: [], limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: {},
    },
    {
        key: 'system-log-compliance', definitionVersion: 1,
        surfaceKey: 'system-logs', domainKey: 'system-log',
        presentation: { labelKey: 'reportCatalog.systemLogCompliance', order: 10 },
        lifecycle: { runEnabled: false, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'internal' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_SYSTEM_LOG_COMPLIANCE', minimumCanaryRuns: 25, minimumCanaryDays: 3, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'technical-ops',
        renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
        sourcePageKey: 'userOperationsSystemLogs', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: [], limits: { maxRecords: 25_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: {},
    },
    // Historical structured-v2 contracts remain addressable for pinned sessions and archives.
    {
        key: 'risk-detailed-portfolio', definitionVersion: 1,
        surfaceKey: 'risk-register', domainKey: 'risk',
        presentation: { labelKey: 'reportCatalog.riskPortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_RISK_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
        sourcePageKey: 'riskOperationsRiskRegister', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['owner', 'inherent', 'residual', 'response', 'description', 'responsePlan', 'causes', 'consequences', 'controls', 'treatments', 'assets'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000 }, archiveType: 'riskDetailedPortfolio',
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: true },
    },
    {
        key: 'asset-detailed-portfolio', definitionVersion: 1,
        surfaceKey: 'asset-register', domainKey: 'asset',
        presentation: { labelKey: 'reportCatalog.assetPortfolio', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ASSET_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'portfolio',
        renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
        sourcePageKey: 'assetInventoryAssets', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['owner', 'type', 'category', 'group', 'cia', 'status', 'supportEnd', 'nextReview', 'definition', 'details', 'businessImpact', 'risks', 'relationshipTypes', 'threats', 'controls'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000 }, archiveType: 'assetDetailedPortfolio',
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: true },
    },
    {
        key: 'risk-record-dossier', definitionVersion: 1,
        surfaceKey: 'risk-detail', domainKey: 'risk',
        presentation: { labelKey: 'reportCatalog.riskDossier', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_RISK_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
        sourcePageKey: 'riskOperationsRiskRegister', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['recordType', 'priority', 'owners', 'probability', 'impact', 'response', 'createdAt', 'updatedAt', 'description', 'responsePlan', 'operationalImpact', 'financialImpactRange', 'reputationalImpact', 'causes', 'consequences', 'controls', 'treatments', 'assetCategories', 'processes', 'assets'],
        limits: { maxRecords: 1 }, archiveType: 'riskRecordDossier',
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: true },
    },
    {
        key: 'asset-record-dossier', definitionVersion: 1,
        surfaceKey: 'asset-detail', domainKey: 'asset',
        presentation: { labelKey: 'reportCatalog.assetDossier', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: true, readExistingArtifacts: true, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ASSET_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'dossier',
        renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
        sourcePageKey: 'assetInventoryAssets', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
        visibleFields: ['owners', 'type', 'category', 'group', 'classification', 'criticality', 'location', 'workflow', 'supportEnd', 'nextReview', 'recoveryTargets', 'currentValue', 'definition', 'details', 'businessImpact', 'dependencies', 'manualWorkarounds', 'personalData', 'risks', 'threats', 'scfControls', 'assetRelationships', 'vendors', 'operationalControls', 'personalDataInventories', 'licenses', 'disposals', 'lifecycle'],
        limits: { maxRecords: 1 }, archiveType: 'assetRecordDossier',
        capabilities: { profile: true, interactive: true, schedule: true, archive: true, snapshot: true, compare: true, aiNarrative: true },
    },
    // Phase 9 retains literal static REPX/query contracts for pinned session TTL and history.
    {
        key: 'control-maturity', definitionVersion: 1,
        surfaceKey: 'isms-controls-soa', domainKey: 'isms-controls',
        presentation: { labelKey: 'reportCatalog.controlMaturity', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_CONTROL_MATURITY', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'ControlMaturityReport.repx', layoutDigest: 'addcc6da6d25ec2d6bb57e654b20b4d1dad468c22a37ad982d9aa4ff75619ff5',
            queryKey: 'control-maturity', queryDigest: '9d246f6f532946a55cb84a36b7c14bc53361115ed49c094615d36d0a21563a99',
            rootProperty: 'controls', dataShape: 'object-array-root',
        },
        sourcePageKey: 'ismsControlsSoa', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['scf_id', 'name', 'domain', 'level', 'level_label'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'iso-compliance-summary', definitionVersion: 1,
        surfaceKey: 'isms-controls-soa', domainKey: 'isms-controls',
        presentation: { labelKey: 'reportCatalog.isoComplianceSummary', order: 20 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ISO_COMPLIANCE_SUMMARY', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'IsoComplianceReport.repx', layoutDigest: 'd44afffefed601ee31a40de0caed98361e228fb43db4910e714edf242ad573c6',
            queryKey: 'iso-compliance-summary', queryDigest: '460db46f02e6bef7a3e49db04b34d36cd4457ff498beb141d5e58f948c205e4c',
            rootProperty: 'clauses', dataShape: 'object-array-root',
        },
        sourcePageKey: 'ismsControlsSoa', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['clause_code', 'title', 'compliance_rate', 'missing_docs', 'missing_controls', 'open_findings'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'soa', definitionVersion: 1,
        surfaceKey: 'isms-controls-soa', domainKey: 'isms-controls',
        presentation: { labelKey: 'reportCatalog.soa', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_SOA', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'SOA_ISO27002.repx', layoutDigest: 'a99b9ec04e68d7bd7de1dcbeae664465d74918e33e0abdcd908984743548581f',
            queryKey: 'soa', queryDigest: 'eb7932a4bc1875e29c59dde8f8d97d6a98797d965d15e567f3b65ff9266557b4',
            rootProperty: 'soa', dataShape: 'object-array-root',
        },
        sourcePageKey: 'ismsControlsSoa', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['control_no', 'control_name', 'domain_identifier', 'tenant_overview_status', 'maturity_line', 'objective_summary', 'related_documents_text', 'comment'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'soa', definitionVersion: 2,
        surfaceKey: 'isms-controls-soa', domainKey: 'isms-controls',
        presentation: { labelKey: 'reportCatalog.soa', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_SOA', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'SOA_ISO27002.repx', layoutDigest: 'a99b9ec04e68d7bd7de1dcbeae664465d74918e33e0abdcd908984743548581f',
            queryKey: 'soa', queryDigest: 'eb7932a4bc1875e29c59dde8f8d97d6a98797d965d15e567f3b65ff9266557b4',
            rootProperty: 'soa', dataShape: 'object-array-root',
        },
        sourcePageKey: 'ismsControlsSoa', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['control_no', 'control_name', 'domain_identifier', 'tenant_overview_status', 'maturity_line', 'objective_summary', 'related_documents_text', 'comment'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'bigdes-gap', definitionVersion: 1,
        surfaceKey: 'bigdes-current-situation-analysis-gap', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesGap', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_GAP', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'BIGDESGapReport.repx', layoutDigest: 'e71c4388863a0f078106df65ab4a57503b2d322cea4ba3efd6e1fe701f2ce84e',
            queryKey: 'bigdes-gap', queryDigest: 'b5e1438429de243322064e05f09741f219677bbcc775549c81ecbb1ac0062d65',
            rootProperty: 'measures', dataShape: 'object-array-root',
        },
        selectorSchema: { properties: { applicabilityId: 'tenant-owned-object-id' }, required: ['applicabilityId'], additionalProperties: false },
        sourcePageKey: 'bigdesCurrentSituationAnalysis', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: [
            'tenant_display_name', 'tenant_logo_url', 'asset_group_name', 'asset_group_no', 'category',
            'category_label_tr', 'criticality_degree', 'generated_date', 'measure_code', 'measure_title',
            'link_source_type', 'link_source_key', 'implementation_status', 'implementation_status_short',
            'implementation_status_label_tr', 'current_explanation', 'target_status', 'target_status_short',
            'target_status_label_tr', 'target_required_work', 'compensatory_text',
        ],
        limits: { maxRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'bigdes-current-situation-analysis-summary', definitionVersion: 1,
        surfaceKey: 'bigdes-current-situation-analysis', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesCurrentSituationAnalysisSummary', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_CURRENT_SITUATION_ANALYSIS_SUMMARY', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'BIGDESCurrentSituationAnalysisSummaryReport.repx', layoutDigest: 'ac3008de7da5b19ff3b0667531248b39834224ddfe80ee4c80fdaf55189bd6db',
            queryKey: 'bigdes-current-situation-analysis-summary', queryDigest: '395528fef1376d78bfafa096e769a520757b80686b1a5b99bea036f19ee2f9fa',
            rootProperty: 'rows', dataShape: 'object-array-root',
        },
        sourcePageKey: 'bigdesCurrentSituationAnalysis', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['tenant_display_name', 'tenant_logo_url', 'generated_date', 'category_label_tr', 'asset_group_name', 'asset_group_no', 'criticality_degree', 'required_measure_count', 'evaluated_measure_count', 'fully_measure_count', 'mostly_measure_count', 'partial_measure_count', 'none_measure_count', 'not_applicable_measure_count'],
        limits: { maxRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'bigdes-current-situation-evaluation-summary', definitionVersion: 1,
        surfaceKey: 'bigdes-current-situation-evaluation', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesCurrentSituationEvaluationSummary', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_CURRENT_SITUATION_EVALUATION_SUMMARY', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'BIGDESCurrentSituationEvaluationSummaryReport.repx', layoutDigest: '29ec1380789d8d08c5ff279cb1e638916d9679d56f2bdf35fd663f4a7276f44f',
            queryKey: 'bigdes-current-situation-evaluation-summary', queryDigest: '249c6b4613792cadb7ddaa376512291ffbdf06d59c4553f4662e5d41df03c205',
            rootProperty: 'rows', dataShape: 'object-array-root',
        },
        sourcePageKey: 'bigdesCurrentSituationEvaluation', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['tenant_display_name', 'tenant_logo_url', 'generated_date', 'category_label_tr', 'asset_group_name', 'asset_group_no', 'criticality_degree', 'required_measure_count', 'effective_measure_count', 'not_effective_measure_count', 'partially_effective_measure_count'],
        limits: { maxRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'bigdes-measure-applicability', definitionVersion: 1,
        surfaceKey: 'bigdes-measure-applicability', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesMeasureApplicability', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_MEASURE_APPLICABILITY', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'BIGDESMeasureApplicabilityReport.repx', layoutDigest: '5c72b3e92256552d29348863b18773cc97dc0770df9bffd366240453a3b9fcf5',
            queryKey: 'bigdes-measure-applicability', queryDigest: '3980f932590e9dce4c704e1784feb30827ba2022527dd88a21f8dd38f7a1413e',
            rootProperty: 'rows', dataShape: 'object-array-root',
        },
        sourcePageKey: 'bigdesMeasureApplicability', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['tenant_display_name', 'tenant_logo_url', 'generated_date', 'category_label_tr', 'asset_group_name', 'asset_group_no', 'criticality_degree', 'in_audit_scope_label_tr', 'personal_data_security_label_tr', 'instant_messaging_security_label_tr', 'cloud_computing_security_label_tr', 'crypto_applications_security_label_tr', 'critical_infrastructure_security_label_tr', 'new_developments_procurement_label_tr', 'os_hardening_label_tr', 'db_hardening_label_tr', 'server_hardening_label_tr'],
        limits: { maxRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'bigdes-current-situation-evaluation', definitionVersion: 1,
        surfaceKey: 'bigdes-current-situation-evaluation-detail', domainKey: 'bigdes',
        presentation: { labelKey: 'reportCatalog.bigdesCurrentSituationEvaluation', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIGDES_CURRENT_SITUATION_EVALUATION', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'BIGDESCurrentSituationEvaluationReport.repx', layoutDigest: '5e57e1debbed2aa030793d6ee01c3a9754667775b59512b480a9dabe7a59faad',
            queryKey: 'bigdes-current-situation-evaluation', queryDigest: '5d890c1d80a6f55d70b5ff3d601835cd482feaba52b37624edb4563b70c2295a',
            rootProperty: 'measures', dataShape: 'object-array-root',
        },
        selectorSchema: { properties: { applicabilityId: 'tenant-owned-object-id' }, required: ['applicabilityId'], additionalProperties: false },
        sourcePageKey: 'bigdesCurrentSituationEvaluation', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['tenant_display_name', 'tenant_logo_url', 'generated_date', 'asset_group_name', 'asset_group_no', 'category', 'category_label_tr', 'criticality_degree', 'measure_code', 'measure_title', 'measure_implementation_status', 'measure_effectiveness_status', 'criticality_level', 'audit_methods_label_tr', 'auditor_comment'],
        limits: { maxRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    // Phase 8 retains the exact static REPX/query contracts for pinned session TTL and history.
    {
        key: 'audit-findings', definitionVersion: 1,
        surfaceKey: 'audit-assessment', domainKey: 'audit',
        presentation: { labelKey: 'reportCatalog.auditFindings', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_AUDIT_FINDINGS', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'FindingsReport.repx', layoutDigest: 'fca66de5c7d01fe3a58a74b9b3c75d2014ddcf8a4ae51fc4d631e120611bb679',
            queryKey: 'audit-findings', queryDigest: 'a34388b76f277a43f4c501066f797ecbc78cc903de1b8e1cbe3a9e20f9e69cb4',
            rootProperty: 'list', dataShape: 'object-array-root',
        },
        sourcePageKey: 'auditFinding', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: [
            'list.name', 'list.control_name', 'list.severity', 'list.status', 'list.is_overdue',
            'list.overdue_label', 'list.assignee', 'list.deadline', 'summary.total_open',
            'summary.open', 'summary.in_progress', 'summary.overdue', 'generated_at',
            'presentation.cover_title', 'presentation.subtitle', 'presentation.footer_text',
            'presentation.accent_rgb', 'presentation.confidentiality_label',
            'presentation.confidentiality_code', 'presentation.confidentiality_color_rgb',
        ],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'auditor-findings', definitionVersion: 1,
        surfaceKey: 'audit-assessment', domainKey: 'audit',
        presentation: { labelKey: 'reportCatalog.auditorFindings', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_AUDITOR_FINDINGS', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'AuditorFindingsReport.repx', layoutDigest: '6304cbc0ccf09b7d264eac746afa8e1f595d1ffe33d2ce7504478598b9a231e0',
            queryKey: 'auditor-findings', queryDigest: 'faa89725591f4970a6563b3a41fbb332ddd02a5abe0b4152cdc20dda1f164f5a',
            rootProperty: 'list', dataShape: 'object-array-root',
        },
        sourcePageKey: 'auditFinding', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: [
            'list.auditor_id', 'list.auditor_name', 'list.opened_count', 'list.closed_count',
            'list.open_count', 'list.avg_closure_days', 'list.closure_rate',
            'summary.total_auditors', 'summary.total_findings', 'summary.avg_closure_global',
            'generated_at', 'presentation.cover_title', 'presentation.subtitle',
            'presentation.footer_text', 'presentation.accent_rgb',
            'presentation.confidentiality_label', 'presentation.confidentiality_code',
            'presentation.confidentiality_color_rgb',
        ],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'document-revisions', definitionVersion: 1,
        surfaceKey: 'report-document', domainKey: 'document',
        presentation: { labelKey: 'reportCatalog.documentRevisions', order: 0, menuVisibility: 'configuration-only' },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_DOCUMENT_REVISIONS', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'DocumentReviewReport.repx', layoutDigest: '3d25ac5c7a352ba26c2d5551a4072f6511255ab0594b4034115dd57f19c8bb59',
            queryKey: 'document-revisions', queryDigest: 'bc5017aeeed9afac7ac67b08cbec1194bdcf6cfe43993075cab8fbe020b477ca',
            rootProperty: 'list', dataShape: 'object-array-root',
        },
        sourcePageKey: 'reportDocument', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['code', 'title', 'version', 'owner', 'next_review', 'days_remaining'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'document-pending', definitionVersion: 1,
        surfaceKey: 'report-document', domainKey: 'document',
        presentation: { labelKey: 'reportCatalog.documentPending', order: 10, menuVisibility: 'configuration-only' },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_DOCUMENT_PENDING', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'DocumentPendingReport.repx', layoutDigest: '98309a849a9ea8feb2e3f2011b99b00908318c1cae87fda2980906c6b4e53ea6',
            queryKey: 'document-pending', queryDigest: '34e76cab2bdaec1601a1351bb9e341eb4c6638cab8c430e312b6cca558ab7844',
            rootProperty: 'list', dataShape: 'object-array-root',
        },
        sourcePageKey: 'reportDocument', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['code', 'title', 'version', 'owner', 'current_step', 'total_steps', 'waiting_for', 'pending_since'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'document-security', definitionVersion: 1,
        surfaceKey: 'document-detail', domainKey: 'document',
        presentation: { labelKey: 'reportCatalog.documentSecurity', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_DOCUMENT_SECURITY', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'technical-ops',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'DocumentSecurityReport.repx', layoutDigest: 'a010e6cf742cac3c53b8020e2687af61b800251089ce6437e232cb5cc4cb615b',
            queryKey: 'document-security', queryDigest: 'b4413778df3f4d5d8524795bc0768b67f236b8b745a30d37d690bede263cd6b9',
            rootProperty: 'list', dataShape: 'object-array-root',
        },
        selectorSchema: { properties: { documentId: 'tenant-owned-object-id' }, required: ['documentId'], additionalProperties: false },
        sourcePageKey: 'reportDocument', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['user_name', 'path', 'ip', 'timestamp'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'overdue-actions', definitionVersion: 1,
        surfaceKey: 'incident-actions', domainKey: 'action',
        sourceSurfaces: { 'incident-actions': 'incidentActions', 'audit-compliance-capa': 'auditComplianceCapa' },
        presentation: { labelKey: 'reportCatalog.overdueActions', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_OVERDUE_ACTIONS', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'OverdueActionsReport.repx', layoutDigest: 'fcb2842ea2f30c4dab6a620f77ecdb0da4526dc898581195786c8c6fe8765aec',
            queryKey: 'overdue-actions', queryDigest: '0fed46df7fe4d2bc383d08eaba8ee2e6e52ff2ea5bbb8e44dbdc1b279e98dc3c',
            rootProperty: 'actions', dataShape: 'object-array-root',
        },
        sourcePageKey: 'incidentActions', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['title', 'owner_name', 'deadline', 'delay_days', 'source_title', 'source_type', 'status', 'progress', 'action_type'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'action-performance', definitionVersion: 1,
        surfaceKey: 'incident-actions', domainKey: 'action',
        sourceSurfaces: { 'incident-actions': 'incidentActions', 'audit-compliance-capa': 'auditComplianceCapa' },
        presentation: { labelKey: 'reportCatalog.actionPerformance', order: 10 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ACTION_PERFORMANCE', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'ActionPerformanceReport.repx', layoutDigest: '8a3202de227a5cdb11e1a14f993cd80f7a72290c564afb6e759597677510409c',
            queryKey: 'action-performance', queryDigest: '9fd9f6cd765d6c7c934403640a450a655e443922aaf450b96260c6ba6360a685',
            rootProperty: 'details', dataShape: 'object-array-root',
        },
        sourcePageKey: 'incidentActions', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: [
            'kpis.avg_closing_days', 'kpis.completion_rate', 'kpis.total_rejected',
            'kpis.total_reassigned', 'kpis.total_actions', 'details.title', 'details.unit_name',
            'details.reassign_count', 'details.reject_count', 'details.duration_days',
            'details.status', 'generated_at',
        ],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'user-workload', definitionVersion: 1,
        surfaceKey: 'incident-actions', domainKey: 'action',
        sourceSurfaces: { 'incident-actions': 'incidentActions', 'audit-compliance-capa': 'auditComplianceCapa', 'my-work': 'incidentActions' },
        presentation: { labelKey: 'reportCatalog.userWorkload', order: 20 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_USER_WORKLOAD', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none', reportClass: 'statutory-template',
        renderer: {
            key: 'static-repx-v1', contractVersion: 1,
            layoutName: 'UserWorkloadReport.repx', layoutDigest: 'e859a7b316e1432b5fa5a521b8e3a368f913d2c48e0f5afbc9b759d640859070',
            queryKey: 'user-workload', queryDigest: '830d9a0a1bdb107c8c1e98afd7902daf9db6e0f7d2dc274cc2931e3793b62adc',
            rootProperty: 'users', dataShape: 'object-array-root',
        },
        sourcePageKey: 'incidentActions', scopeModes: ['tenant'], outputFormats: ['pdf'],
        visibleFields: ['user_name', 'open_count', 'completed_count', 'overdue_count', 'total_count'],
        limits: { maxRecords: 2_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    // Phase 7 retains literal structured-v2 snapshots for detailed-session TTL compatibility.
    {
            key: 'document-detailed-portfolio',
            definitionVersion: 1,
            surfaceKey: 'report-document',
            domainKey: 'document',
            presentation: { labelKey: 'reportCatalog.documentPortfolio', order: 30, menuVisibility: 'configuration-only' },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_DOCUMENT_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none',
            reportClass: 'portfolio',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'reportDocument',
            scopeModes: ['filtered', 'selected'],
            outputFormats: ['pdf', 'docx'],
            visibleFields: ['code', 'title', 'type', 'classification', 'unit', 'status', 'workflow', 'review', 'description', 'documentMetadata', 'createdAt', 'updatedAt'],
            limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 },
            archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'vendor-detailed-portfolio', definitionVersion: 1,
            surfaceKey: 'vendor-register', domainKey: 'vendor',
            presentation: { labelKey: 'reportCatalog.vendorPortfolio', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_VENDOR_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'portfolio',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'vendorManagementVendors', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['name', 'type', 'category', 'criticality', 'status', 'city', 'businessOwner', 'securityOwner', 'contractExpiration', 'nextAssessmentReviewDate', 'linkedAssetCount'],
            limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'vendor-record-dossier', definitionVersion: 1,
            surfaceKey: 'vendor-detail', domainKey: 'vendor',
            presentation: { labelKey: 'reportCatalog.vendorDossier', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_VENDOR_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'dossier',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'vendorManagementVendors', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['name', 'type', 'category', 'criticality', 'status', 'city', 'businessOwner', 'securityOwner', 'contractExpiration', 'nextAssessmentReviewDate', 'contractSummary', 'bcmSummary', 'linkedAssets'],
            limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'incident-detailed-portfolio', definitionVersion: 1,
            surfaceKey: 'incident-register', domainKey: 'incident',
            presentation: { labelKey: 'reportCatalog.incidentPortfolio', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_INCIDENT_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'portfolio',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'incidentManagementIncidents', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['code', 'title', 'type', 'category', 'severity', 'impact', 'urgency', 'status', 'dates', 'slaState', 'primaryUnit', 'assignee', 'affectedCount'],
            limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'incident-record-dossier', definitionVersion: 1,
            surfaceKey: 'incident-detail', domainKey: 'incident',
            presentation: { labelKey: 'reportCatalog.incidentDossier', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_INCIDENT_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'dossier',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'incidentManagementIncidents', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['code', 'title', 'type', 'category', 'severity', 'impact', 'urgency', 'status', 'dates', 'slaState', 'primaryUnit', 'assignee', 'affectedLabels', 'rootCauseSummary', 'responseSummary', 'evidenceMetadata'],
            limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'bia-detailed-portfolio', definitionVersion: 1,
            surfaceKey: 'bia-register', domainKey: 'bia',
            presentation: { labelKey: 'reportCatalog.biaPortfolio', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIA_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'portfolio',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'bcmBia', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['biaId', 'name', 'process', 'unit', 'location', 'criticality', 'state', 'rto', 'rpo', 'mtpd', 'impact', 'review', 'dependencyCounts'],
            limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'bia-record-dossier', definitionVersion: 1,
            surfaceKey: 'bia-detail', domainKey: 'bia',
            presentation: { labelKey: 'reportCatalog.biaDossier', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BIA_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'dossier',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'bcmBia', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['biaId', 'name', 'process', 'unit', 'location', 'criticality', 'state', 'rto', 'rpo', 'mtpd', 'impactMatrix', 'dependencies', 'resources', 'documentMetadata'],
            limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'bcp-detailed-portfolio', definitionVersion: 1,
            surfaceKey: 'bcp-register', domainKey: 'bcp',
            presentation: { labelKey: 'reportCatalog.bcpPortfolio', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BCP_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'portfolio',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'bcmBcp', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['code', 'name', 'state', 'status', 'process', 'unit', 'location', 'review'],
            limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'bcp-record-dossier', definitionVersion: 1,
            surfaceKey: 'bcp-detail', domainKey: 'bcp',
            presentation: { labelKey: 'reportCatalog.bcpDossier', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_BCP_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'dossier',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'bcmBcp', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['code', 'name', 'state', 'status', 'bia', 'process', 'unit', 'location', 'author', 'reviewer', 'approver', 'review', 'recovery', 'roles', 'contactLocationMetadata', 'documentNames', 'exerciseLabels'],
            limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'exercise-detailed-portfolio', definitionVersion: 1,
            surfaceKey: 'exercise-register', domainKey: 'exercise',
            presentation: { labelKey: 'reportCatalog.exercisePortfolio', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_EXERCISE_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'portfolio',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'bcmExercises', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['code', 'name', 'type', 'status', 'dates', 'duration', 'location', 'objectiveSummary', 'resultSummary', 'counts', 'score', 'issues'],
            limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'exercise-record-dossier', definitionVersion: 1,
            surfaceKey: 'exercise-detail', domainKey: 'exercise',
            presentation: { labelKey: 'reportCatalog.exerciseDossier', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_EXERCISE_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'dossier',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'bcmExercises', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['code', 'name', 'type', 'status', 'bcpLabels', 'dates', 'duration', 'location', 'objectiveSummary', 'resultSummary', 'counts', 'score', 'issues', 'evaluations', 'improvements', 'documentNames'],
            limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'gdpr-consent-detailed-portfolio', definitionVersion: 1,
            surfaceKey: 'gdpr-consent-register', domainKey: 'gdpr-consent',
            presentation: { labelKey: 'reportCatalog.gdprConsentPortfolio', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_GDPR_CONSENT_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'portfolio',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'gdprObtainingExplicitConsent', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['pseudonymousRowKey', 'method', 'status', 'disclosureSignedAt', 'consentSignedAt', 'validCount', 'withdrawnCount', 'expiredCount'],
            limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'gdpr-consent-record-dossier', definitionVersion: 1,
            surfaceKey: 'gdpr-consent-detail', domainKey: 'gdpr-consent',
            presentation: { labelKey: 'reportCatalog.gdprConsentDossier', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_GDPR_CONSENT_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'dossier',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'gdprObtainingExplicitConsent', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['pseudonymousRowKey', 'method', 'status', 'disclosureSignedAt', 'consentSignedAt', 'disclosureDocumentPresent', 'consentDocumentPresent', 'consentAuditFacts'],
            limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'gdpr-dsr-detailed-portfolio', definitionVersion: 1,
            surfaceKey: 'gdpr-dsr-register', domainKey: 'gdpr-dsr',
            presentation: { labelKey: 'reportCatalog.gdprDsrPortfolio', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_GDPR_DSR_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'portfolio',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'gdprDataSubjectRequest', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['referenceNumber', 'requestType', 'status', 'receivedAt', 'deadline', 'respondedAt', 'unit', 'deadlineState'],
            limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'gdpr-dsr-record-dossier', definitionVersion: 1,
            surfaceKey: 'gdpr-dsr-register', domainKey: 'gdpr-dsr',
            presentation: { labelKey: 'reportCatalog.gdprDsrDossier', order: 20 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_GDPR_DSR_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'dossier',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'gdprDataSubjectRequest', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['referenceNumber', 'requestType', 'status', 'receivedAt', 'deadline', 'respondedAt', 'unit', 'deadlineState', 'subjectSummary', 'descriptionSummary', 'responseSummary', 'attachmentCount', 'attachmentClasses', 'notesCount'],
            limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'gdpr-breach-detailed-portfolio', definitionVersion: 1,
            surfaceKey: 'gdpr-breach-register', domainKey: 'gdpr-breach',
            presentation: { labelKey: 'reportCatalog.gdprBreachPortfolio', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_GDPR_BREACH_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'portfolio',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'gdprDataBreach', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['referenceNumber', 'redactedTitle', 'status', 'severity', 'detectedAt', 'affectedCount', 'confidentialityImpact', 'integrityImpact', 'availabilityImpact', 'notificationState', 'notificationDate'],
            limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'gdpr-breach-record-dossier', definitionVersion: 1,
            surfaceKey: 'gdpr-breach-register', domainKey: 'gdpr-breach',
            presentation: { labelKey: 'reportCatalog.gdprBreachDossier', order: 20 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_GDPR_BREACH_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'dossier',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'gdprDataBreach', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['referenceNumber', 'redactedTitle', 'status', 'severity', 'detectedAt', 'affectedCount', 'confidentialityImpact', 'integrityImpact', 'availabilityImpact', 'notificationState', 'notificationDate', 'categoryTypeAggregates', 'causeSummary', 'containmentSummary', 'remediationSummary', 'preventionSummary'],
            limits: { maxRecords: 1, maxPayloadBytes: 2_097_152 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'ticket-detailed-portfolio', definitionVersion: 1,
            surfaceKey: 'ticket-register', domainKey: 'ticket',
            presentation: { labelKey: 'reportCatalog.ticketPortfolio', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_TICKET_DETAILED_PORTFOLIO', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'portfolio',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'ticketing', scopeModes: ['filtered', 'selected'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['ticketNumber', 'mode', 'source', 'status', 'priority', 'assignmentState', 'dates', 'taskCount', 'commentCount', 'attachmentCount'],
            limits: { maxRecords: 25, maxSelectedRecords: 25, maxPayloadBytes: 1_048_576 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    {
            key: 'ticket-record-dossier', definitionVersion: 1,
            surfaceKey: 'ticket-detail', domainKey: 'ticket',
            presentation: { labelKey: 'reportCatalog.ticketDossier', order: 10 },
            lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
            rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_TICKET_RECORD_DOSSIER', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
            legacyCompatibility: 'none', reportClass: 'dossier',
            renderer: { key: 'programmatic-structured-v2', contractVersion: 2 },
            sourcePageKey: 'ticketing', scopeModes: ['single'], outputFormats: ['pdf', 'docx'],
            visibleFields: ['ticketNumber', 'mode', 'source', 'status', 'priority', 'assignmentState', 'dates', 'taskCount', 'commentCount', 'attachmentCount'],
            limits: { maxRecords: 1, maxPayloadBytes: 1_048_576 }, archiveType: null,
            capabilities: { profile: true, interactive: true },
        },
    // Phase 6 keeps literal list-v1 snapshots readable while their session TTL drains.
    {
        key: 'risk-filtered',
        definitionVersion: 1,
        surfaceKey: 'risk-register',
        domainKey: 'risk',
        presentation: { labelKey: 'reportCatalog.riskFiltered', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_RISK_FILTERED', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'generic-list',
        renderer: { key: 'programmatic-list-v1', contractVersion: 1 },
        sourcePageKey: 'riskOperationsRiskRegister',
        scopeModes: ['filtered', 'selected'],
        outputFormats: ['pdf', 'docx', 'xlsx'],
        visibleFields: [
            'riskId', 'riskNature', 'title', 'priority', 'probability', 'impact',
            'score', 'riskLevel', 'status', 'responseStrategy', 'owners',
        ],
        limits: { maxRecords: 25_000, maxSelectedRecords: 5_000 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'asset-filtered',
        definitionVersion: 1,
        surfaceKey: 'asset-register',
        domainKey: 'asset',
        presentation: { labelKey: 'reportCatalog.assetFiltered', order: 0 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_ASSET_FILTERED', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'generic-list',
        renderer: { key: 'programmatic-list-v1', contractVersion: 1 },
        sourcePageKey: 'assetInventoryAssets',
        scopeModes: ['filtered', 'selected'],
        outputFormats: ['pdf', 'docx', 'xlsx'],
        visibleFields: [
            'assetId', 'assetName', 'category', 'assetCategory', 'assetGroup',
            'classificationLevel', 'usingStatus', 'owners', 'location',
            'purchaseDate', 'currentValue', 'currency',
        ],
        limits: { maxRecords: 25_000, maxSelectedRecords: 5_000 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
    {
        key: 'document-filtered',
        definitionVersion: 1,
        surfaceKey: 'report-document',
        domainKey: 'document',
        presentation: { labelKey: 'reportCatalog.documentFiltered', order: 20 },
        lifecycle: { runEnabled: true, scheduleEnabled: false, readExistingArtifacts: false, rolloutAudience: 'all' },
        rollout: { owner: 'reporting-platform', flag: 'REPORT_DEFINITION_DOCUMENT_FILTERED', minimumCanaryRuns: 0, minimumCanaryDays: 0, maximumAuthorizationMismatches: 0 },
        legacyCompatibility: 'none',
        reportClass: 'generic-list',
        renderer: { key: 'programmatic-list-v1', contractVersion: 1 },
        sourcePageKey: 'reportDocument',
        scopeModes: ['filtered', 'selected'],
        outputFormats: ['pdf', 'docx', 'xlsx'],
        visibleFields: ['code', 'title', 'type', 'classification', 'unit', 'status', 'workflow', 'review', 'description', 'documentMetadata', 'createdAt', 'updatedAt'],
        limits: { maxRecords: 5_000, maxSelectedRecords: 5_000, maxPayloadBytes: 2_097_152 },
        archiveType: null,
        capabilities: { profile: true, profileOptional: true, interactive: true },
    },
];

export const REPORT_DEFINITIONS = deepFreeze([
    ...rawHistoricalReportDefinitions.map((definition) => ReportDefinitionSchema.parse(definition)),
    ...Object.values(REPORT_CATALOG),
]);

export function assertReportCatalogInvariants(catalog = REPORT_CATALOG, definitions = REPORT_DEFINITIONS) {
    const versions = new Set();
    const currentByKey = new Map();
    const rendererContracts = new Map([
        ['programmatic-list-v1', 1],
        ['programmatic-structured-v2', 2],
        ['programmatic-structured-v3', 3],
        ['static-repx-v1', 1],
    ]);

    for (const definition of definitions) {
        const versionKey = `${definition.key}@${definition.definitionVersion}`;
        if (versions.has(versionKey)) throw new Error(`Duplicate report definition version: ${versionKey}`);
        versions.add(versionKey);
        if (rendererContracts.get(definition.renderer.key) !== definition.renderer.contractVersion) {
            throw new Error(`Invalid renderer contract: ${versionKey}`);
        }
        if (!definition.rollout.owner || !definition.lifecycle.rolloutAudience
            || definition.rollout.minimumCanaryRuns < 0
            || definition.rollout.minimumCanaryDays < 0
            || definition.rollout.maximumAuthorizationMismatches < 0) {
            throw new Error(`Invalid lifecycle rollout metadata: ${versionKey}`);
        }
        if (!definition.lifecycle.runEnabled && definition.lifecycle.scheduleEnabled) {
            throw new Error(`Disabled report is scheduled: ${versionKey}`);
        }
        if (catalog[definition.key]?.definitionVersion === definition.definitionVersion) {
            if (currentByKey.has(definition.key)) {
                throw new Error(`Multiple current report definitions: ${definition.key}`);
            }
            currentByKey.set(definition.key, definition);
        }
    }

    for (const [key, definition] of Object.entries(catalog)) {
        if (currentByKey.get(key) !== definition) {
            throw new Error(`Catalog current selection mismatch: ${key}`);
        }
    }
    if (currentByKey.size !== Object.keys(catalog).length) {
        throw new Error('Every catalog report key must have exactly one current definition');
    }
    return true;
}

assertReportCatalogInvariants();

const definitionVersions = new Map();
for (const definition of REPORT_DEFINITIONS) {
    const key = `${definition.key}@${definition.definitionVersion}`;
    if (definitionVersions.has(key)) throw new Error(`Duplicate report definition version: ${key}`);
    definitionVersions.set(key, definition);
}

export const REPORT_DEFINITION_HISTORY = deepFreeze(Object.fromEntries(
    REPORT_KEYS.map((reportKey) => [
        reportKey,
        REPORT_DEFINITIONS
            .filter((definition) => definition.key === reportKey)
            .sort((left, right) => left.definitionVersion - right.definitionVersion),
    ]),
));

function toDetailedReportDefinition(definition) {
    return deepFreeze({
        key: definition.key,
        domain: definition.domainKey,
        kind: definition.reportClass,
        labelKey: definition.presentation.labelKey,
        sourcePageKey: definition.sourcePageKey,
        scopeModes: definition.scopeModes,
        outputFormats: definition.outputFormats,
        visibleFields: definition.visibleFields,
        archiveType: definition.archiveType,
        rendererKey: definition.renderer.key,
        capabilities: {
            profile: definition.capabilities.profile,
            interactive: definition.capabilities.interactive,
            schedule: definition.capabilities.schedule,
            archive: definition.capabilities.archive,
        },
    });
}

function toDetailedReportProfileDefinition(definition) {
    return deepFreeze({
        ...toDetailedReportDefinition(definition),
        capabilities: definition.capabilities,
    });
}

export function projectDetailedReportProfileCatalog(catalog) {
    return deepFreeze(Object.fromEntries(
        Object.values(catalog)
            .filter((definition) => definition.capabilities.profile)
            .map((definition) => [definition.key, toDetailedReportProfileDefinition(definition)]),
    ));
}

export const DETAILED_REPORT_PROFILE_CATALOG = projectDetailedReportProfileCatalog(REPORT_CATALOG);

export function getReportDefinition(reportKey, definitionVersion = null) {
    if (definitionVersion == null) {
        return Object.hasOwn(REPORT_CATALOG, reportKey) ? REPORT_CATALOG[reportKey] : null;
    }
    return definitionVersions.get(`${reportKey}@${definitionVersion}`) || null;
}

export function getReportDefinitionsForSurface(surfaceKey, { includeDisabled = false } = {}) {
    return Object.values(REPORT_CATALOG)
        .filter((definition) => reportDefinitionSupportsSurface(definition, surfaceKey))
        .filter((definition) => includeDisabled || definition.lifecycle.runEnabled)
        .sort((left, right) => left.presentation.order - right.presentation.order);
}

export function getDetailedReportDefinition(reportKey) {
    const definition = DETAILED_REPORT_PROFILE_CATALOG[reportKey];
    return definition?.capabilities?.schedule && definition?.capabilities?.snapshot ? definition : null;
}

export function getDetailedReportProfileDefinition(reportKey) {
    return Object.hasOwn(DETAILED_REPORT_PROFILE_CATALOG, reportKey)
        ? DETAILED_REPORT_PROFILE_CATALOG[reportKey]
        : null;
}
